// Request detail view: ParentLinkageSection, DetailTabContent,
// ESignatureModal, RequestDetailModal.
//
// These four components together render the full request detail
// experience: the editable details tab, the parent agreement linkage
// UI, the e-signature envelope status modal, and the parent modal
// that contains them all.
//
// Heavy logic: editable status/assignment, parent recheck on
// counterparty change, e-signature webhook polling, attachment
// upload, document version management, AI extraction validation,
// reviewer feedback, and DocuSign integration.

import { useState, useEffect, useCallback, useRef, useMemo, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, hasPermission } from "../utils";
import { apiCall, apiCallEx, downloadDocument, openInWord } from "../apiClient";
import { OrgBadge } from "./OrgBadge";
import { HandoffModal } from "./HandoffModal";
import { useClientScope } from "../contexts/ClientScopeContext";
import { formatDisplayNumber } from "../utils/displayNumber";
import { sfLink } from "../salesforce";
import { ALL_STATUSES, STATUS_COLORS, DEPARTMENTS, SIGNNOW_ENABLED } from "../constants";
import { LAWYERS } from "../sampleData";
import { renderMarkdown } from "./markdown";
import type { RequestRow, UserRow, LawyerRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, TabBar, EmptyState, RefreshBtn, VoiceInput } from "./primitives";
import { CommunicationsTab, SendToCounterpartyModal } from "./Communications";

const ParentLinkageSection = ({ parentInfo = null, detail = null, req = null, canEdit = false, onNav: onNavProp = undefined, refreshDetail = undefined }: { parentInfo?: any; detail?: any; req?: RequestRow | null; canEdit?: boolean; onNav?: any; refreshDetail?: any }) => {
  const onNav = onNavProp || (() => {});
  const [showLinkUI, setShowLinkUI] = useState(false);
  const [linkMode, setLinkMode] = useState<any>(null); // 'repo', 'request', 'online'
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [onlineUrl, setOnlineUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [allowedTypes, setAllowedTypes] = useState<any>(null); // null = no rule, [] = unrestricted
  const [ruleInfo, setRuleInfo] = useState<any>(null);
  const [linkError, setLinkError] = useState("");
  const searchTimer = useRef<any>(null);

  // Fetch allowed parent types when link UI opens
  useEffect(() => {
    if (!showLinkUI || !req) return;
    const category = req!.agreementCategory || detail?.agreement_category || '';
    const aType = req!.type || detail?.agreement_type || detail?.request_type || '';
    if (!category || !aType) { setAllowedTypes(null); return; }
    apiCall('/parent-rules/check', { method: 'POST', body: JSON.stringify({ agreement_category: category, agreement_type: aType, counterparty: req!.counterparty || '' }) })
      .then(r => {
        if (r?.requires_parent && r.rule) {
          setAllowedTypes(r.rule.required_parent_types);
          setRuleInfo(r.rule);
        } else {
          setAllowedTypes(null);
          setRuleInfo(null);
        }
      });
  }, [showLinkUI, req]);

  const doSearch = (q: string) => {
    setSearchQ(q); setLinkError("");
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      if (linkMode === 'repo') {
        const data = await apiCall(`/contracts?search=${encodeURIComponent(q)}`);
        let results = (data?.flat || []).slice(0, 20).map((c: any) => ({ id: c.id, label: c.name, sub: `${c.contract_type} · ${c.counterparty_entity}`, type: c.contract_type, source: 'repository' }));
        // Filter by allowed parent types if rule exists
        if (allowedTypes && allowedTypes.length > 0) {
          const allowed = allowedTypes.map((t: string) => t.toLowerCase());
          results = results.filter((r: any) => r.type && allowed.includes(r.type.toLowerCase()));
        }
        setSearchResults(results.slice(0, 10));
      } else if (linkMode === 'request') {
        const data = await apiCall(`/requests?search=${encodeURIComponent(q)}&limit=20`);
        let results = (data?.requests || []).map((r: any) => ({ id: r.id, label: r.title, sub: `${r.request_type} · ${r.counterparty} · ${r.status}`, type: r.agreement_type || r.request_type, source: 'in_flight' }));
        if (allowedTypes && allowedTypes.length > 0) {
          const allowed = allowedTypes.map((t: string) => t.toLowerCase());
          results = results.filter((r: any) => r.type && allowed.includes(r.type.toLowerCase()));
        }
        setSearchResults(results.slice(0, 10));
      }
      setSearching(false);
    }, 400);
  };

  const saveLink = async (match: any) => {
    setSaving(true); setLinkError("");
    const body = match.source === 'repository' ? { parent_contract_id: match.id } : { parent_request_id: match.id };
    const token = localStorage.getItem("kontractai_token");
    try {
      const res = await fetch(`/api/requests/${req!.id}/parent-linkage`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setLinkError(data.error || 'Failed to link parent'); setSaving(false); return; }
    } catch (e: any) { setLinkError('Failed to link parent'); setSaving(false); return; }
    setSaving(false); setShowLinkUI(false); setSearchQ(""); setSearchResults([]);
    if (refreshDetail) refreshDetail?.(req!.id || '');
  };

  const saveOnlineTerms = async () => {
    setSaving(true);
    await apiCall(`/requests/${req!.id}/parent-linkage`, { method: 'PUT', body: JSON.stringify({ parent_is_online_terms: true, online_terms_url: onlineUrl.trim() || null }) });
    setSaving(false); setShowLinkUI(false); setOnlineUrl("");
    if (refreshDetail) refreshDetail?.(req!.id || '');
  };

  const removeLink = async () => {
    if (!window.confirm("Remove parent linkage?")) return;
    setSaving(true);
    await apiCall(`/requests/${req!.id}/parent-linkage`, { method: 'PUT', body: JSON.stringify({}) });
    setSaving(false);
    if (refreshDetail) refreshDetail?.(req!.id || '');
  };

  // Master agreements are top-level by definition — they don't have a parent.
  // Hide the entire parent linkage UI for them.
  const reqType = String((req as any)?.type || (req as any)?.agreement_type || (req as any)?.request_type || '').toLowerCase();
  const isMasterAgreement = /master\s+(services?\s+)?agreement|^msa\b|\bmsa\b|master\s+supply|master\s+license|master\s+subscription/.test(reqType);
  if (isMasterAgreement) return null;

  const hasParent = parentInfo && (parentInfo.type === 'repository' || parentInfo.type === 'in_flight' || parentInfo.type === 'online_terms');

  // Linked parent display
  if (hasParent && !showLinkUI) {
    return (
      <div style={{ marginTop: 16 }}>
        {parentInfo.type === 'repository' && (() => {
          const dn = parentInfo.display_number;
          const code = (parentInfo.client_display_code || '').toUpperCase();
          const dnLabel = (dn && code) ? `CON-${code}-${String(dn).padStart(4, '0')}` : (dn ? `CON-${String(dn).padStart(4, '0')}` : null);
          return (
            <div style={{ padding: 14, backgroundColor: 'rgba(45,160,80,.06)', border: '1px solid rgba(45,160,80,.2)', borderLeft: '4px solid rgba(45,160,80,.6)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontSize: 12, fontWeight: 700, color: T.grn, marginBottom: 4 }}>Parent Agreement{dnLabel ? <span style={{ marginLeft: 8, fontFamily: 'monospace', color: T.acc, fontWeight: 500 }}>{dnLabel}</span> : null}</div><div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{parentInfo.name}</div><div style={{ fontSize: 11, color: T.txM }}>{parentInfo.contract_type} · {parentInfo.counterparty}{parentInfo.effective_date ? ` · Effective: ${fmtDate(parentInfo.effective_date)}` : ''}</div></div>
              <div style={{ display: 'flex', gap: 6 }}>
                {onNav && <button onClick={() => onNav?.('repo', parentInfo.id)} style={{ fontSize: 11, color: T.acc, background: 'none', border: `1px solid ${T.acc}`, borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View</button>}
                {canEdit && <button onClick={() => setShowLinkUI(true)} style={{ fontSize: 10, color: T.txD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Change</button>}
              </div>
            </div>
          );
        })()}
        {parentInfo.type === 'in_flight' && (() => {
          const dn = parentInfo.display_number;
          const code = (parentInfo.client_display_code || '').toUpperCase();
          const dnLabel = (dn && code) ? `REQ-${code}-${String(dn).padStart(4, '0')}` : (dn ? `REQ-${String(dn).padStart(4, '0')}` : null);
          return (
            <div style={{ padding: 14, backgroundColor: 'rgba(26,138,158,.06)', border: '1px solid rgba(26,138,158,.2)', borderLeft: '4px solid rgba(26,138,158,.6)', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontSize: 12, fontWeight: 700, color: T.cyn, marginBottom: 4 }}>Parent Agreement (In Progress){dnLabel ? <span style={{ marginLeft: 8, fontFamily: 'monospace', color: T.acc, fontWeight: 500 }}>{dnLabel}</span> : null}</div><div style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{parentInfo.title}</div><div style={{ fontSize: 11, color: T.txM }}>{parentInfo.agreement_type} · {parentInfo.counterparty} · {parentInfo.status}</div></div>
              <div style={{ display: 'flex', gap: 6 }}>
                {onNav && <button onClick={() => onNav?.('request', parentInfo.id)} style={{ fontSize: 11, color: T.acc, background: 'none', border: `1px solid ${T.acc}`, borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>View</button>}
                {canEdit && <button onClick={() => setShowLinkUI(true)} style={{ fontSize: 10, color: T.txD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Change</button>}
              </div>
            </div>
          );
        })()}
        {parentInfo.type === 'online_terms' && (
          <div style={{ padding: 14, backgroundColor: 'rgba(167,139,250,.06)', border: '1px solid rgba(167,139,250,.2)', borderLeft: '4px solid rgba(167,139,250,.6)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.prp, marginBottom: 4 }}>
                  Parent Agreement ({parentInfo.online_terms_party === 'company' ? "Company's Online Terms" : "Counterparty's Online Terms"})
                </div>
                {parentInfo.url ? <a href={parentInfo.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: T.acc, textDecoration: 'underline' }}>{parentInfo.url}</a> : <div style={{ fontSize: 11, color: T.orn, fontStyle: 'italic' }}>URL not yet provided</div>}
              </div>
              {canEdit && <button onClick={() => setShowLinkUI(true)} style={{ fontSize: 10, color: T.txD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Change</button>}
            </div>
          </div>
        )}
        {detail?.missing_parent_explanation && (
          <div style={{ marginTop: 8, padding: 12, backgroundColor: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.25)', borderLeft: '4px solid rgba(245,166,35,.7)', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.orn, marginBottom: 4 }}>Requester Note</div>
            <div style={{ fontSize: 12, color: T.tx, whiteSpace: 'pre-wrap' }}>{detail.missing_parent_explanation}</div>
          </div>
        )}
      </div>
    );
  }

  // No parent linked — show placeholder with "Add Parent" option
  if (!hasParent && !showLinkUI) {
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ padding: 14, backgroundColor: T.bg3, border: `1px dashed ${T.brd}`, borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><div style={{ fontSize: 12, fontWeight: 700, color: T.txD }}>Parent Agreement</div><div style={{ fontSize: 11, color: T.txM }}>No parent agreement linked</div></div>
          {canEdit && <Btn v="secondary" sz="sm" onClick={() => setShowLinkUI(true)}>Link Parent</Btn>}
        </div>
        {detail?.missing_parent_explanation && (
          <div style={{ marginTop: 8, padding: 12, backgroundColor: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.25)', borderLeft: '4px solid rgba(245,166,35,.7)', borderRadius: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.orn, marginBottom: 4 }}>Requester Note</div>
            <div style={{ fontSize: 12, color: T.tx, whiteSpace: 'pre-wrap' }}>{detail.missing_parent_explanation}</div>
          </div>
        )}
      </div>
    );
  }

  // Link UI — search and select
  return (
    <div style={{ marginTop: 16, padding: 14, border: `1px solid ${T.acc}40`, borderRadius: 8, backgroundColor: T.accS }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.acc }}>Link Parent Agreement</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {hasParent && <button onClick={() => { removeLink(); }} disabled={saving} style={{ fontSize: 10, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Remove Link</button>}
          <button onClick={() => { setShowLinkUI(false); setLinkMode(null); setSearchQ(""); setSearchResults([]); }} style={{ fontSize: 10, color: T.txD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        </div>
      </div>
      {/* Mode selection */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[{ v: 'repo', l: 'Repository Contract' }, { v: 'request', l: 'In-Flight Request' }, { v: 'online', l: 'Online Terms' }].map(m => (
          <button key={m.v} onClick={() => { setLinkMode(m.v); setSearchQ(""); setSearchResults([]); }} style={{ flex: 1, padding: '8px 6px', borderRadius: 5, border: `1px solid ${linkMode === m.v ? T.acc : T.brd}`, backgroundColor: linkMode === m.v ? T.accS : T.bg2, color: linkMode === m.v ? T.acc : T.txM, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{m.l}</button>
        ))}
      </div>
      {/* Rule info — show allowed parent types */}
      {allowedTypes && allowedTypes.length > 0 && (linkMode === 'repo' || linkMode === 'request') && (
        <div style={{ fontSize: 10, color: T.txM, marginBottom: 8, padding: '6px 8px', backgroundColor: T.bg3, borderRadius: 4 }}>
          Allowed parent types: <strong style={{ color: T.tx }}>{allowedTypes.join(', ')}</strong>
        </div>
      )}
      {/* Error message */}
      {linkError && <div style={{ fontSize: 11, color: T.red, padding: '6px 8px', backgroundColor: T.redS, borderRadius: 4, marginBottom: 8 }}>{linkError}</div>}
      {/* Search for repo or request */}
      {(linkMode === 'repo' || linkMode === 'request') && (
        <div>
          <input value={searchQ} onChange={e => doSearch(e.target.value)} placeholder={linkMode === 'repo' ? "Search contracts by name, counterparty..." : "Search requests by title, counterparty..."} style={{ width: '100%', padding: '8px 10px', borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
          {searching && <div style={{ fontSize: 11, color: T.txD, padding: 4 }}>Searching...</div>}
          {searchResults.map(r => (
            <div key={r.id} onClick={() => saveLink(r)} style={{ padding: '8px 10px', backgroundColor: T.bg2, borderRadius: 5, marginBottom: 4, cursor: 'pointer', border: `1px solid ${T.brd}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{r.label}</div>
              <div style={{ fontSize: 10, color: T.txM }}>{r.sub}</div>
            </div>
          ))}
          {searchQ.length >= 2 && !searching && searchResults.length === 0 && <div style={{ fontSize: 11, color: T.txD, padding: 4 }}>No matches found{allowedTypes ? ` (filtered to: ${allowedTypes.join(', ')})` : ''}</div>}
        </div>
      )}
      {/* Online terms */}
      {linkMode === 'online' && (
        <div>
          <input value={onlineUrl} onChange={e => setOnlineUrl(e.target.value)} placeholder="https://counterparty.com/terms (optional)" style={{ width: '100%', padding: '8px 10px', borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 8 }} />
          <div style={{ fontSize: 10, color: T.txM, marginBottom: 8 }}>Designate the counterparty's online terms as the parent agreement. URL can be added later.</div>
          <Btn v="primary" sz="sm" onClick={() => { saveOnlineTerms(); }} disabled={saving}>{saving ? "Saving..." : "Set as Online Terms"}</Btn>
        </div>
      )}
    </div>
  );
};

const DetailTabContent = ({ req = null, canEdit = false, user = null, lawyer = null, lawyers = [], sc = null, onUpdate = undefined, refreshDetail = undefined, parentInfo = null, childRequests = [], requestDetail: detail = null, onNav: onNavProp = undefined }: { req?: RequestRow | null; canEdit?: boolean; user?: UserRow | null; lawyer?: LawyerRow | null; lawyers?: LawyerRow[]; sc?: any; onUpdate?: any; refreshDetail?: any; parentInfo?: any; childRequests?: RequestRow[]; requestDetail?: any; onNav?: any }) => {
  const onNav = onNavProp || (() => {});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [editContractTypes, setEditContractTypes] = useState<any[]>([]);
  const [editBusinessUnits, setEditBusinessUnits] = useState<any[]>([]);
  const [parentRecheck, setParentRecheck] = useState<any>(null);
  const [showParentRelink, setShowParentRelink] = useState(false);
  const parentRecheckTimer = useRef<any>(null);
  const f = (k: string) => form[k] ?? "";
  const set = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  // Fetch dropdowns on mount
  useEffect(() => {
    apiCall('/contract-types').then(r => { if (r?.types) setEditContractTypes(r.types); });
    apiCall('/settings/business-units').then(r => { if (r?.businessUnits) setEditBusinessUnits(r.businessUnits); });
  }, []);

  // Counterparty change triggers parent re-validation
  useEffect(() => {
    if (!editing || !form.counterparty || form.counterparty === (req?.counterparty || '')) { setShowParentRelink(false); return; }
    if (parentRecheckTimer.current) clearTimeout(parentRecheckTimer.current);
    parentRecheckTimer.current = setTimeout(async () => {
      const result = await apiCall('/parent-rules/check', { method: 'POST', body: JSON.stringify({ agreement_category: form.agreementCategory || req?.agreementCategory, agreement_type: form.type || req?.type, counterparty: form.counterparty }) });
      if (result?.requires_parent) { setParentRecheck(result); setShowParentRelink(true); }
      else { setParentRecheck(null); setShowParentRelink(false); }
    }, 500);
    return () => { if (parentRecheckTimer.current) clearTimeout(parentRecheckTimer.current); };
  }, [editing, form.counterparty]);

  // Item 31: early-return guard + non-nullable alias. Below this line all
  // `request.foo` have been converted to `request.foo` (TypeScript narrows
  // `request` to RequestRow because of the null-check above). Do not
  // reintroduce `req!` in this component — it defeats the guard.
  if (!req) return null;
  const request: RequestRow = req;

  const startEdit = () => {
    setForm({
      counterparty: request.counterparty || "", counterpartyContact: request.counterpartyContact || "",
      counterpartyAddress: request.counterpartyAddress || "",
      counterpartyEntityType: request.counterpartyEntityType || "",
      counterpartyJurisdiction: request.counterpartyJurisdiction || "",
      type: request.type || "", agreementCategory: request.agreementCategory || "", business_unit: request.business_unit || "",
      targetDate: request.targetDate || "", description: request.description || "", business_owner: request.businessOwner || "",
      department: request.dept || "",
    });
    setEditing(true); setShowParentRelink(false);
  };

  const cancelEdit = () => { setEditing(false); setForm({}); setShowParentRelink(false); };

  const saveEdit = async () => {
    setSaving(true);
    const body: Record<string, any> = {};
    if (f("counterparty") !== (request.counterparty || "")) body.counterparty = f("counterparty");
    if (f("counterpartyContact") !== (request.counterpartyContact || "")) body.counterparty_contact = f("counterpartyContact");
    if (f("counterpartyAddress") !== (request.counterpartyAddress || "")) body.counterparty_address = f("counterpartyAddress");
    if (f("counterpartyEntityType") !== (request.counterpartyEntityType || "")) body.counterparty_entity_type = f("counterpartyEntityType");
    if (f("counterpartyJurisdiction") !== (request.counterpartyJurisdiction || "")) body.counterparty_jurisdiction = f("counterpartyJurisdiction");
    if (f("type") !== (request.type || "")) { body.request_type = f("type"); body.agreement_type = f("type"); body.title = f("type"); }
    if (f("agreementCategory") !== (request.agreementCategory || "")) body.agreement_category = f("agreementCategory");
    if (f("business_unit") !== (request.business_unit || "")) body.business_unit = f("business_unit");
    if (f("targetDate") !== (request.targetDate || "")) body.target_date = f("targetDate") || null;
    if (f("description") !== (request.description || "")) body.description = f("description");
    if (f("business_owner") !== (request.businessOwner || "")) body.business_owner = f("business_owner");
    if (f("department") !== (request.dept || "")) body.department = f("department");
    // Include parent linkage if changed via re-validation
    if (form.parent_contract_id !== undefined) body.parent_contract_id = form.parent_contract_id;
    if (form.parent_request_id !== undefined) body.parent_request_id = form.parent_request_id;
    if (form.parent_is_online_terms !== undefined) body.parent_is_online_terms = form.parent_is_online_terms;

    if (Object.keys(body).length > 0) {
      const result = await apiCall(`/requests/${request.id}`, { method: "PUT", body: JSON.stringify(body) });
      if (result) {
        onUpdate?.(request.id, { _refreshOnly: true, title: body.title || request.title, counterparty: f("counterparty") || request.counterparty });
        if (refreshDetail) refreshDetail?.(request.id || '');
      }
    }
    setSaving(false);
    setEditing(false);
  };

  const inpStyle: CSSProperties = { width: "100%", padding: "6px 8px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const lblStyle: CSSProperties = { fontSize: 9, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 };
  const valStyle: CSSProperties = { fontSize: 12, color: T.tx, fontWeight: 500 };
  const ReadField = ({ label, value }: { label?: any; value?: any }) => <div><div style={lblStyle}>{label}</div><div style={valStyle}>{value || "N/A"}</div></div>;

  if (!editing) {
    return (
      <div>
        {canEdit && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}><Btn v="secondary" sz="sm" onClick={startEdit}>Edit Details</Btn></div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {[["Status", <Badge c={sc.c} b={sc.b} size="md" dot>{request.status}</Badge>], ["Priority", <Badge c={request.priority === "Urgent" ? T.red : request.priority === "High" ? T.orn : T.txM} b={request.priority === "Urgent" ? T.redS : request.priority === "High" ? T.ornS : T.bg4} size="md">{request.priority}</Badge>], ["Agreement Category", request.agreementCategory || "Not specified"], ["Type", request.type], ["Counterparty", request.counterparty], ["Counterparty Address", request.counterpartyAddress || "—"], ["Counterparty Entity Type", request.counterpartyEntityType || "—"], ["Counterparty Jurisdiction", request.counterpartyJurisdiction || "—"], ["Contact", request.counterpartyContact || "N/A"], ["Business Unit", request.business_unit || "Not specified"], ["Requester", request.requester], ["Assigned", lawyer?.name || "Unassigned"], ["Target", fmtDate(request.targetDate) || "None"], ["Created", fmtDate(request.created)], ["Updated", fmtDate(request.updated)]].map(([l, v], i) => (
            <div key={i}><div style={lblStyle}>{l}</div><div style={valStyle}>{v}</div></div>
          ))}
          {request.dept && <ReadField label="Department" value={request.dept} />}
          {request.businessOwner && <ReadField label="Business Owner" value={request.businessOwner} />}
        </div>
        {request.sfOppId && (
          <div style={{ gridColumn: "1/-1", padding: "8px 12px", backgroundColor: "rgba(0,136,209,.06)", border: "1px solid rgba(0,136,209,.15)", borderRadius: 5, marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#0088D1", fontWeight: 700, fontSize: 13 }}>☁</span>
              <div><div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase" }}>Salesforce Opportunity</div><div style={{ fontSize: 12, color: T.tx, fontWeight: 500 }}>{request.sfOppName || request.sfOppId}</div></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {request.sfAmount && <Badge c={T.grn} b={T.grnS}>${(request.sfAmount).toLocaleString()}</Badge>}
              <a href={sfLink(request.sfOppId)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#0088D1", textDecoration: "none", fontWeight: 600 }}>Open in Salesforce ↗</a>
            </div>
          </div>
        )}
        {request.description && <div style={{ padding: 10, backgroundColor: T.bg3, borderRadius: 5, marginTop: 12 }}><div style={lblStyle}>Description</div><div style={{ fontSize: 12, color: T.tx, lineHeight: 1.5, whiteSpace: "pre-line" }}>{request.description}</div></div>}
        {/* Parent Agreement Section */}
        <ParentLinkageSection parentInfo={parentInfo} detail={detail} req={req} canEdit={canEdit} onNav={onNav} refreshDetail={refreshDetail} />
        {/* Repository Record (only for fully-executed requests) */}
        {request.status === 'Fully Executed' && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>Repository Record</div>
            {detail?.repositoryContract ? (
              <div onClick={() => onNav && onNav?.('repo', detail.repositoryContract.id)} style={{ padding: '10px 14px', backgroundColor: T.bg3, borderRadius: 6, cursor: onNav ? 'pointer' : 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: `1px solid ${T.grnS}` }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{detail.repositoryContract.name}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>Imported into the repository</div>
                </div>
                <span style={{ fontSize: 11, color: T.acc, fontWeight: 600 }}>View →</span>
              </div>
            ) : (
              <div style={{ padding: '10px 14px', backgroundColor: T.bg3, borderRadius: 6, fontSize: 11, color: T.orn, fontStyle: 'italic', border: `1px dashed ${T.brd}` }}>
                Awaiting repository import
              </div>
            )}
          </div>
        )}
        {/* Child Agreements (grouped by type) */}
        {childRequests && childRequests.length > 0 && (() => {
          const groups: Record<string, RequestRow[]> = {};
          for (const child of childRequests) {
            const key = (child as any).request_type || (child as any).agreement_type || 'Other';
            if (!groups[key]) groups[key] = [];
            groups[key].push(child);
          }
          const groupKeys = Object.keys(groups).sort();
          return (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>Associated Child Agreements</div>
              {groupKeys.map((groupKey) => (
                <div key={groupKey} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.txM, marginBottom: 4, textTransform: 'uppercase' }}>{groupKey} ({groups[groupKey].length})</div>
                  {groups[groupKey].map((child: any) => {
                    const seq = child.child_sequence_number;
                    const dn = child.display_number;
                    const code = (child.client_display_code || '').toUpperCase();
                    const dnLabel = (dn && code) ? `REQ-${code}-${String(dn).padStart(4, '0')}` : (dn ? `REQ-${String(dn).padStart(4, '0')}` : null);
                    return (
                      <div key={child.id} onClick={() => onNav && onNav?.('request', child.id)} style={{ padding: '10px 14px', backgroundColor: T.bg3, borderRadius: 6, marginBottom: 6, cursor: onNav ? 'pointer' : 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>
                            {dnLabel && <span style={{ fontFamily: 'monospace', color: T.acc, marginRight: 8 }}>{dnLabel}</span>}
                            {child.title}
                            {seq != null && <span style={{ marginLeft: 8, fontSize: 10, color: T.txD }}>#{seq}</span>}
                          </div>
                          <div style={{ fontSize: 11, color: T.txM }}>{child.agreement_type || child.request_type} · {child.counterparty}</div>
                        </div>
                        <Badge c={STATUS_COLORS[child.status || '']?.c || T.txM} b={STATUS_COLORS[child.status || '']?.b || T.bg4}>{child.status}</Badge>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()}
        {/* Governance summary */}
        {(request as any).governance_type && (
          <div style={{ marginTop: 16, padding: '10px 14px', backgroundColor: T.bg3, borderRadius: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 4, textTransform: 'uppercase' }}>Governance</div>
            <div style={{ fontSize: 12, color: T.tx }}>
              {(request as any).governance_type === 'negotiated_master' && 'Governed by negotiated master agreement'}
              {(request as any).governance_type === 'counterparty_online_terms' && (
                <>Governed by counterparty's online terms{(request as any).online_terms_url ? <>: <a href={(request as any).online_terms_url} target="_blank" rel="noopener noreferrer" style={{ color: T.acc }}>{(request as any).online_terms_url}</a></> : ''}</>
              )}
              {(request as any).governance_type === 'company_online_terms' && (
                <>Governed by company's own online terms{(request as any).online_terms_url ? <>: <a href={(request as any).online_terms_url} target="_blank" rel="noopener noreferrer" style={{ color: T.acc }}>{(request as any).online_terms_url}</a></> : ''}</>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 10 }}>
        <Btn v="ghost" sz="sm" onClick={cancelEdit}>Cancel</Btn>
        <Btn v="primary" sz="sm" onClick={() => { saveEdit(); }} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <ReadField label="Status" value={<Badge c={sc.c} b={sc.b} size="md" dot>{request.status}</Badge>} />
        <ReadField label="Priority" value={<Badge c={request.priority === "Urgent" ? T.red : request.priority === "High" ? T.orn : T.txM} b={request.priority === "Urgent" ? T.redS : request.priority === "High" ? T.ornS : T.bg4} size="md">{request.priority}</Badge>} />
        <ReadField label="Assigned" value={lawyer?.name || "Unassigned"} />
        <ReadField label="Title" value={request.title} />
        <div><div style={lblStyle}>Counterparty</div><input value={f("counterparty")} onChange={e => set("counterparty", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Contact</div><input value={f("counterpartyContact")} onChange={e => set("counterpartyContact", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Counterparty Address</div><input value={f("counterpartyAddress")} onChange={e => set("counterpartyAddress", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Counterparty Entity Type</div><input value={f("counterpartyEntityType")} onChange={e => set("counterpartyEntityType", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Counterparty Jurisdiction</div><input value={f("counterpartyJurisdiction")} onChange={e => set("counterpartyJurisdiction", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Agreement Category</div>
          <select value={f("agreementCategory")} onChange={e => { set("agreementCategory", e.target.value); set("type", ""); }} style={inpStyle}>
            <option value="">Select...</option><option value="Customer">Customer</option><option value="Vendor">Vendor</option><option value="Corporate">Corporate</option>
          </select>
        </div>
        <div><div style={lblStyle}>Type</div>
          <select value={f("type")} onChange={e => set("type", e.target.value)} disabled={!f("agreementCategory")} style={{ ...inpStyle, opacity: f("agreementCategory") ? 1 : 0.5 }}>
            <option value="">{f("agreementCategory") ? "Select type..." : "Select category first..."}</option>
            {editContractTypes.filter((t: any) => t.category === f("agreementCategory")).map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            {f("agreementCategory") && <optgroup label="Amendments">{editContractTypes.filter((t: any) => t.category === f("agreementCategory")).map(t => <option key={`a-${t.id}`} value={`Amendment to ${t.name}`}>Amendment to {t.name}</option>)}</optgroup>}
          </select>
        </div>
        <div><div style={lblStyle}>Business Unit</div>
          <select value={f("business_unit")} onChange={e => set("business_unit", e.target.value)} style={inpStyle}>
            <option value="">Select...</option>
            {editBusinessUnits.map((bu: any) => <option key={bu.id} value={bu.name}>{bu.name}</option>)}
          </select>
        </div>
        <div><div style={lblStyle}>Department</div>
          <select value={f("department")} onChange={e => set("department", e.target.value)} style={inpStyle}>
            <option value="">Select...</option>{DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div><div style={lblStyle}>Business Owner</div><input value={f("business_owner")} onChange={e => set("business_owner", e.target.value)} style={inpStyle} /></div>
        <div><div style={lblStyle}>Target Date</div><input type="date" value={f("targetDate")} onChange={e => set("targetDate", e.target.value)} style={inpStyle} /></div>
        <ReadField label="Requester" value={request.requester} />
        <ReadField label="Created" value={fmtDate(request.created)} />
        <ReadField label="Updated" value={fmtDate(request.updated)} />
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={lblStyle}>Description</div>
        <textarea value={f("description")} onChange={e => set("description", e.target.value)} rows={4} style={{ ...inpStyle, resize: "vertical" }} />
      </div>
      {/* Parent re-validation on counterparty change */}
      {showParentRelink && parentRecheck && (
        <div style={{ marginTop: 12, padding: 14, backgroundColor: 'rgba(245,166,35,.06)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.orn, marginBottom: 8 }}>Counterparty changed — parent agreement re-validation</div>
          <div style={{ fontSize: 11, color: T.txM, marginBottom: 10 }}>A {parentRecheck.rule.child_type} requires: {parentRecheck.rule.required_parent_types.join(' or ')}</div>
          {parentRecheck.matches.length > 0 ? parentRecheck.matches.map((m: any) => (
            <div key={m.id} onClick={() => { if (m.source === 'repository') setForm((p: any) => ({ ...p, parent_contract_id: m.id, parent_request_id: null })); else setForm((p: any) => ({ ...p, parent_request_id: m.id, parent_contract_id: null })); setShowParentRelink(false); }}
              style={{ padding: '8px 12px', backgroundColor: T.bg3, borderRadius: 4, marginBottom: 4, cursor: 'pointer', border: `1px solid ${T.brd}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{m.name || m.title}</div>
              <div style={{ fontSize: 10, color: T.txM }}>{m.contract_type || m.agreement_type} · {m.counterparty_entity || m.counterparty}</div>
            </div>
          )) : <div style={{ fontSize: 11, color: T.red }}>No matching parent found. Save will clear existing parent linkage.</div>}
          {parentRecheck.rule.online_terms_eligible && (
            <div onClick={() => { setForm((p: any) => ({ ...p, parent_is_online_terms: true, parent_contract_id: null, parent_request_id: null })); setShowParentRelink(false); }}
              style={{ padding: '8px 12px', backgroundColor: 'rgba(167,139,250,.06)', border: '1px solid rgba(167,139,250,.2)', borderRadius: 4, marginTop: 6, cursor: 'pointer', fontSize: 12, color: T.prp }}>
              Parent is counterparty's Online Terms
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ SPEND TAB (in-house only, admin/legalops) ═══════════════════ */
const SpendTabContent = ({ requestId, canWrite }: { requestId: string; canWrite: boolean }) => {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({ amount: '', category: 'legal_fees', entry_date: new Date().toISOString().slice(0, 10), vendor: '', description: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiCall(`/spend/entries?request_id=${requestId}`);
    setEntries(r?.entries || []);
    setLoading(false);
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  const fmtMoney = (v: any) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prettyCategory = (c: string) => ({ legal_fees: 'Legal Fees', filing_fees: 'Filing Fees', settlement: 'Settlement', other: 'Other' } as Record<string, string>)[c] || c;
  const total = entries.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const submit = async () => {
    if (!form.amount || Number(form.amount) < 0) return alert('Amount must be >= 0');
    if (!form.entry_date) return alert('Entry date required');
    const r = await apiCall('/spend/entries', {
      method: 'POST',
      body: JSON.stringify({
        request_id: requestId,
        amount: Number(form.amount),
        category: form.category,
        entry_date: form.entry_date,
        vendor: form.vendor || null,
        description: form.description || null,
      }),
    });
    if (r?.entry) {
      setShowAdd(false);
      setForm({ amount: '', category: 'legal_fees', entry_date: new Date().toISOString().slice(0, 10), vendor: '', description: '' });
      load();
    } else {
      alert(r?.error || 'Failed to add spend entry');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this spend entry?')) return;
    await apiCall(`/spend/entries/${id}`, { method: 'DELETE' });
    load();
  };

  const inputStyle: CSSProperties = { padding: '6px 10px', borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Spend on this Request</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: T.txM }}>Total: <span style={{ color: T.tx, fontWeight: 600 }}>{fmtMoney(total)}</span></div>
          {canWrite && <Btn v="primary" sz="sm" onClick={() => setShowAdd(true)}>+ Add Spend</Btn>}
        </div>
      </div>

      {loading && entries.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: T.txM, fontSize: 12 }}>Loading...</div> :
        entries.length === 0 ? <EmptyState icon="◈" msg="No spend recorded on this request yet" /> : (
          <Card style={{ overflowX: 'auto', padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
                  {['Date', 'Amount', 'Category', 'Vendor', 'Description', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                    <td style={{ padding: '8px 10px', color: T.tx }}>{fmtDate(e.entry_date)}</td>
                    <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(e.amount)}</td>
                    <td style={{ padding: '8px 10px', color: T.tx }}>{prettyCategory(e.category)}</td>
                    <td style={{ padding: '8px 10px', color: T.txM }}>{e.vendor || ''}</td>
                    <td style={{ padding: '8px 10px', color: T.txM, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description || ''}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {canWrite && <button onClick={() => remove(e.id)} style={{ fontSize: 10, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

      {showAdd && (
        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Spend Entry" w={480}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 700 }}>Amount *</div><input type="number" min={0} step="0.01" value={form.amount} onChange={e => setForm((p: any) => ({ ...p, amount: e.target.value }))} placeholder="0.00" style={inputStyle} /></div>
            <div><div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 700 }}>Category</div><select value={form.category} onChange={e => setForm((p: any) => ({ ...p, category: e.target.value }))} style={inputStyle}><option value="legal_fees">Legal Fees</option><option value="filing_fees">Filing Fees</option><option value="settlement">Settlement</option><option value="other">Other</option></select></div>
            <div><div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 700 }}>Entry Date *</div><input type="date" value={form.entry_date} onChange={e => setForm((p: any) => ({ ...p, entry_date: e.target.value }))} style={inputStyle} /></div>
            <div><div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 700 }}>Vendor</div><input value={form.vendor} onChange={e => setForm((p: any) => ({ ...p, vendor: e.target.value }))} placeholder="(optional)" style={inputStyle} /></div>
            <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 10, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 700 }}>Description</div><textarea value={form.description} onChange={e => setForm((p: any) => ({ ...p, description: e.target.value }))} rows={2} placeholder="(optional)" style={{ ...inputStyle, resize: 'vertical' }} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <Btn v="secondary" sz="sm" onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn v="primary" sz="sm" onClick={() => { submit(); }}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* ═══════════════════ E-SIGNATURE MODAL ═══════════════════ */
const ESignatureModal = ({ open = false, onClose = undefined, envelope = null }: { open?: boolean; onClose?: () => void; envelope?: any }) => {
  const [tab, setTab] = useState("status");
  const [liveData, setLiveData] = useState<any>(null);
  const [audit, setAudit] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [editingSigner, setEditingSigner] = useState<any>(null); // { index, name, email }
  const [correcting, setCorrecting] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);

  const esignColors: Record<string, { c: string; b: string }> = { sent: { c: T.blu, b: T.bluS }, delivered: { c: T.ylw, b: T.ylwS }, completed: { c: T.grn, b: T.grnS }, declined: { c: T.red, b: T.redS }, voided: { c: T.txD, b: T.bg3 }, created: { c: T.txM, b: T.bg4 } };
  const signerColor = (s: string) => s === "completed" ? T.grn : s === "declined" ? T.red : s === "delivered" ? T.ylw : s === "sent" ? T.blu : T.txD;

  const loadLive = useCallback(async () => {
    if (!envelope?.envelope_id) return;
    setLoading(true);
    const data = await apiCall(`/esign/envelope/${envelope.envelope_id}/live`);
    if (data) setLiveData(data);
    setLoading(false);
  }, [envelope?.envelope_id]);

  const loadAudit = useCallback(async () => {
    if (!envelope?.envelope_id) return;
    setLoading(true);
    const data = await apiCall(`/esign/envelope/${envelope.envelope_id}/audit`);
    if (data) setAudit(data.events || []);
    setLoading(false);
  }, [envelope?.envelope_id]);

  useEffect(() => {
    if (open && envelope?.envelope_id) { loadLive(); }
  }, [open, envelope?.envelope_id, loadLive]);

  useEffect(() => {
    if (open && tab === "audit" && !audit) loadAudit();
  }, [open, tab, audit, loadAudit]);

  // Auto-refresh live status every 15s while open
  useEffect(() => {
    if (!open || !envelope?.envelope_id) return;
    const interval = setInterval(loadLive, 15000);
    return () => clearInterval(interval);
  }, [open, envelope?.envelope_id, loadLive]);

  const handleCorrectSigner = async () => {
    if (!editingSigner || !editingSigner.email) return;
    setCorrecting(true); setResendMsg("");
    const recipientId = editingSigner.routingOrder || editingSigner.index + 1;
    const r = await apiCall(`/esign/envelope/${envelope.envelope_id}/correct`, {
      method: "POST", body: JSON.stringify({ recipientId, email: editingSigner.email, name: editingSigner.name }),
    });
    setCorrecting(false);
    if (r?.success) {
      setResendMsg(r.message);
      setEditingSigner(null);
      setTimeout(() => setResendMsg(""), 4000);
      loadLive();
    } else {
      setResendMsg("Correction failed: " + (r?.error || "Unknown error"));
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true); setResendMsg("");
    const r = await apiCall(`/esign/envelope/${envelope.envelope_id}/void`, {
      method: "POST", body: JSON.stringify({ reason: voidReason.trim() }),
    });
    setVoiding(false);
    if (r?.success) {
      setResendMsg("Envelope voided successfully");
      setShowVoidConfirm(false); setVoidReason("");
      loadLive();
    } else {
      setResendMsg("Void failed: " + (r?.error || "Unknown error"));
    }
  };

  const handleResend = async () => {
    if (!window.confirm("Resend the signing invitation to all pending signers?")) return;
    setResending(true); setResendMsg("");
    const r = await apiCall(`/esign/envelope/${envelope.envelope_id}/resend`, { method: "POST" });
    setResending(false);
    if (r?.success) { setResendMsg("Signing invitation resent successfully"); setTimeout(() => setResendMsg(""), 4000); loadLive(); }
    else setResendMsg("Resend failed: " + (r?.error || "Unknown error"));
  };

  if (!envelope) return null;
  const signers = liveData?.signers || (typeof envelope.signers === "string" ? JSON.parse(envelope.signers) : envelope.signers) || [];
  const env = liveData?.envelope || {};
  const envStatus = env.status || envelope.status || "unknown";
  const sc = esignColors[envStatus] || { c: T.txM, b: T.bg4 };

  return (
    <Modal open={open} onClose={onClose} title="E-Signature Details" w={680}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Badge c={sc.c} b={sc.b} size="md">{envStatus}</Badge>
          {/* Provider pill: shows which e-sign platform owns this envelope. */}
          {envelope.provider && (
            <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, backgroundColor: T.bg3, color: T.txM, fontWeight: 700, textTransform: "uppercase", border: `1px solid ${T.brd}` }}>
              {envelope.provider === "signnow" ? "SignNow" : "DocuSign"}
            </span>
          )}
          {liveData?.environment === "sandbox" && <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, backgroundColor: T.ornS, color: T.orn, fontWeight: 700, textTransform: "uppercase" }}>Demo</span>}
          {envelope.sent_by_name && <span style={{ fontSize: 11, color: T.txD }}>Sent by {envelope.sent_by_name}</span>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["sent", "delivered", "created"].includes(envStatus) && (
            <Btn v="primary" sz="sm" onClick={() => { handleResend(); }} disabled={resending} style={{ backgroundColor: "#5C2D91" }}>
              {resending ? "Resending..." : "Resend Invitation"}
            </Btn>
          )}
          {["sent", "delivered", "created"].includes(envStatus) && (
            <Btn v="danger" sz="sm" onClick={() => setShowVoidConfirm(true)}>Void Envelope</Btn>
          )}
          <RefreshBtn onClick={() => { loadLive(); }} label={envelope.provider === "signnow" ? "Query SignNow" : "Query DocuSign"} />
        </div>
      </div>
      {resendMsg && <div style={{ padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 12, backgroundColor: resendMsg.includes("success") || resendMsg.includes("voided") ? T.grnS : T.redS, color: resendMsg.includes("success") || resendMsg.includes("voided") ? T.grn : T.red }}>{resendMsg}</div>}
      {/* Void Confirmation */}
      {showVoidConfirm && (
        <div style={{ padding: 14, backgroundColor: T.redS, border: `1px solid ${T.red}40`, borderRadius: 6, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 8 }}>Void this envelope?</div>
          <div style={{ fontSize: 11, color: T.txM, marginBottom: 10 }}>This will cancel the signing process for all signers. This action cannot be undone.</div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", marginBottom: 3 }}>Reason (required)</div>
            <input value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="e.g., Incorrect document, terms changed, sent to wrong party..." style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.red}40`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn v="ghost" sz="sm" onClick={() => { setShowVoidConfirm(false); setVoidReason(""); }}>Cancel</Btn>
            <Btn v="danger" sz="sm" onClick={() => { handleVoid(); }} disabled={voiding || !voidReason.trim()}>{voiding ? "Voiding..." : "Confirm Void"}</Btn>
          </div>
        </div>
      )}
      {/* Voided state — offer to send new envelope */}
      {envStatus === "voided" && (
        <div style={{ padding: 14, backgroundColor: T.bg3, border: `1px solid ${T.brd}`, borderRadius: 6, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 2 }}>Envelope Voided</div>
              <div style={{ fontSize: 11, color: T.txM }}>{env.voidedReason || envelope.error_message || "This envelope has been voided."}</div>
            </div>
            <Btn v="primary" sz="sm" onClick={() => { onClose?.(); }} style={{ backgroundColor: "#5C2D91" }}>Send New Envelope</Btn>
          </div>
        </div>
      )}

      {/* Envelope Info */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16, padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
        {[
          ["Subject", env.subject || envelope.email_subject || "N/A"],
          ["Created", env.createdDateTime ? fmtDate(env.createdDateTime) : fmtDate(envelope.created_at)],
          ["Sent", env.sentDateTime ? fmtDate(env.sentDateTime) : fmtDate(envelope.sent_at)],
          ...(env.completedDateTime ? [["Completed", fmtDate(env.completedDateTime)]] : []),
          ...(env.voidedDateTime ? [["Voided", fmtDate(env.voidedDateTime)]] : []),
          ["Envelope ID", <span style={{ fontFamily: "monospace", fontSize: 10 }}>{(envelope.envelope_id || "").substring(0, 18)}...</span>],
        ].map(([l, v], i) => (
          <div key={i}><div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 2 }}>{l}</div><div style={{ fontSize: 11, color: T.tx, fontWeight: 500 }}>{v}</div></div>
        ))}
      </div>

      {/* Tabs */}
      <TabBar tabs={[{ id: "status", label: "Signers" }, { id: "audit", label: "Audit Trail" }]} active={tab} onChange={setTab} />

      {loading && <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading...</div>}

      {/* Signers Tab */}
      {tab === "status" && !loading && (
        <div>
          {signers.map((s: any, i: number) => {
            const canCorrect = s.status !== "completed" && s.status !== "declined" && ["sent", "delivered", "created"].includes(envStatus);
            const isEditing = editingSigner?.index === i;
            return (
            <div key={i} style={{ padding: "12px 14px", backgroundColor: T.bg3, borderRadius: 6, marginBottom: 8, border: `1px solid ${isEditing ? T.acc : T.brd}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: signerColor(s.status), display: "inline-block" }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{s.name}</span>
                  <span style={{ fontSize: 11, color: T.txD }}>{s.email}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {canCorrect && !isEditing && <button onClick={() => setEditingSigner({ index: i, name: s.name, email: s.email, routingOrder: s.routingOrder || i + 1 })} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Edit</button>}
                  <Badge c={signerColor(s.status)} b={signerColor(s.status) + "18"}>{s.status || "pending"}</Badge>
                </div>
              </div>
              {isEditing && (
                <div style={{ padding: "10px 0 8px", borderTop: `1px solid ${T.brd}`, marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.acc, textTransform: "uppercase", marginBottom: 6 }}>Correct Signer</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", marginBottom: 2 }}>Name</div>
                      <input value={editingSigner.name} onChange={e => setEditingSigner((p: any) => ({ ...p, name: e.target.value }))} style={{ width: "100%", padding: "6px 8px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", marginBottom: 2 }}>Email</div>
                      <input value={editingSigner.email} onChange={e => setEditingSigner((p: any) => ({ ...p, email: e.target.value }))} type="email" style={{ width: "100%", padding: "6px 8px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn v="ghost" sz="sm" onClick={() => setEditingSigner(null)}>Cancel</Btn>
                    <Btn v="primary" sz="sm" onClick={() => { handleCorrectSigner(); }} disabled={correcting || !editingSigner.email}>{correcting ? "Updating..." : "Update & Resend"}</Btn>
                  </div>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 10, color: T.txM }}>
                <div>Order: <strong style={{ color: T.tx }}>{s.routingOrder || i + 1}</strong></div>
                <div>Delivered: <strong style={{ color: s.deliveredDateTime ? T.tx : T.txD }}>{s.deliveredDateTime ? new Date(s.deliveredDateTime).toLocaleString() : "Not yet"}</strong></div>
                <div>Signed: <strong style={{ color: s.signedDateTime ? T.grn : T.txD }}>{s.signedDateTime ? new Date(s.signedDateTime).toLocaleString() : "Not yet"}</strong></div>
                {s.declinedDateTime && <div style={{ gridColumn: "1/-1", color: T.red }}>Declined: {new Date(s.declinedDateTime).toLocaleString()}{s.declinedReason ? ` — ${s.declinedReason}` : ""}</div>}
                {s.errorDetails && <div style={{ gridColumn: "1/-1", color: T.red }}>Error: {typeof s.errorDetails === "object" ? JSON.stringify(s.errorDetails) : s.errorDetails}</div>}
              </div>
            </div>
            );
          })}
          {envelope.status === "completed" && !envelope.contract_id && (
            <div style={{ padding: 10, backgroundColor: T.ylwS, borderRadius: 6, marginTop: 8, fontSize: 12, color: T.ylw, fontWeight: 600, textAlign: "center" }}>Awaiting import in Execution Queue</div>
          )}
          {envelope.status === "completed" && envelope.contract_id && (
            <div style={{ padding: 10, backgroundColor: T.grnS, borderRadius: 6, marginTop: 8, fontSize: 12, color: T.grn, fontWeight: 600, textAlign: "center" }}>Imported to contract repository</div>
          )}
        </div>
      )}

      {/* Audit Trail Tab */}
      {tab === "audit" && !loading && (
        <div>
          {!audit && <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading audit trail...</div>}
          {audit && audit.length === 0 && <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>No audit events found</div>}
          {audit && audit.length > 0 && (
            <div style={{ maxHeight: 400, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
                    {["Time", "Action", "User", "Email", "Details"].map(h => (
                      <th key={h} style={{ padding: "6px 8px", textAlign: "left", fontSize: 9, textTransform: "uppercase", color: T.txD, fontWeight: 700, letterSpacing: ".03em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e: any, i: number) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.brdSubtle}` }}>
                      <td style={{ padding: "5px 8px", color: T.txD, fontSize: 10, whiteSpace: "nowrap" }}>{e.time ? new Date(e.time).toLocaleString() : ""}</td>
                      <td style={{ padding: "5px 8px", color: T.tx, fontWeight: 500 }}>{e.action}</td>
                      <td style={{ padding: "5px 8px", color: T.tx }}>{e.user}</td>
                      <td style={{ padding: "5px 8px", color: T.txD }}>{e.email}</td>
                      <td style={{ padding: "5px 8px", color: T.txM, fontSize: 10 }}>{e.reason || e.source || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <RefreshBtn onClick={() => { loadAudit(); }} label="Reload Audit" />
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ═══════════════════ REQUEST DETAIL ═══════════════════ */
export const RequestDetailModal = ({ req = null, open = false, onClose = undefined, onUpdate = undefined, user = null, onNavigateToDrafting = undefined, onNav: onNavProp = undefined, initialTab = null, initialThreadId = null, initialVersionId = null, initialEnvelopeId = null, onInitialNavConsumed = undefined }: { req?: RequestRow | null; open?: boolean; onClose?: () => void; onUpdate?: any; user?: UserRow | null; onNavigateToDrafting?: any; onNav?: any; initialTab?: string | null; initialThreadId?: string | null; initialVersionId?: string | null; initialEnvelopeId?: string | null; onInitialNavConsumed?: (() => void) | undefined }) => {
  // Tab ids that exist in this modal; unknown tab params fall back to "details".
  // (reviewers/signature live inside the details tab — a future wave may split them.)
  const VALID_TABS = new Set(["details", "versions", "activity", "communications", "manage", "spend"]);
  const resolvedInitialTab = initialTab && VALID_TABS.has(initialTab) ? initialTab : "details";
  const onNav = onNavProp || (() => {});
  const [tab, setTab] = useState("details");
  const [messageUnreadCount, setMessageUnreadCount] = useState<number>(0);
  const [newStatus, setNewStatus] = useState(req?.status || "");
  const [statusReason, setStatusReason] = useState("");
  const [assignTo, setAssignTo] = useState(req?.assignedTo || "");
  const [draftLabel, setDraftLabel] = useState("internal_draft");
  const [draftFile, setDraftFile] = useState<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [newFileNotes, setNewFileNotes] = useState("");
  const [note, setNote] = useState("");
  const [versions, setVersions] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [lawyers, setLawyers] = useState(LAWYERS);
  const [handoffDirection, setHandoffDirection] = useState<"firm" | "in_house" | null>(null);
  const clientScope = useClientScope();
  const myOrgType = clientScope.organization?.org_type;
  const hasPriorEscalation = activity.some((a: any) => a.action === "Escalated to outside counsel");
  // Wave B belt-and-suspenders: in-house deployments never see escalate/return.
  const editionAllowsHandoff = clientScope.edition !== "in_house";
  const canEscalate = editionAllowsHandoff && myOrgType === "client";
  const canReturn = editionAllowsHandoff && (myOrgType === "firm" || myOrgType === "internal") && hasPriorEscalation;
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState("");
  // Cancel-and-supersede tool (admin only). Modal pulls available clients
  // on open; submit posts to /api/admin/requests/:id/cancel-and-supersede.
  const [showCancelSupersede, setShowCancelSupersede] = useState(false);
  const [csClients, setCsClients] = useState<any[]>([]);
  const [csNewClientId, setCsNewClientId] = useState<string>("");
  const [csReason, setCsReason] = useState("");
  const [csPreserve, setCsPreserve] = useState(true);
  const [csSubmitting, setCsSubmitting] = useState(false);
  const [spFolderUrl, setSpFolderUrl] = useState<any>(null);
  const [requestDetail, setRequestDetail] = useState<any>(null); // full request object from API
  const [parentInfo, setParentInfo] = useState<any>(null);
  const [childRequests, setChildRequests] = useState<any[]>([]);
  const [spSyncing, setSpSyncing] = useState(false);
  const [refreshingVersions, setRefreshingVersions] = useState(false);
  const fileInputRef = useRef<any>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareVersionId, setShareVersionId] = useState<any>(null);
  const [shareForm, setShareForm] = useState<Record<string, any>>({ recipient_name: "", recipient_email: "", recipient_org: "", expires_in_days: "7" });
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [existingShares, setExistingShares] = useState<any[]>([]);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showEsignDetail, setShowEsignDetail] = useState(false);
  const [signSigners, setSignSigners] = useState<any[]>([{ name: "", email: "", role: "Counterparty", order: 1 }, { name: "", email: "", role: "Company", order: 1 }]);
  const [signingOrder, setSigningOrder] = useState("parallel");
  const [ccRecipients, setCcRecipients] = useState<any[]>([]); // "parallel" or "sequential"
  const [signProvider, setSignProvider] = useState<"docusign" | "signnow">("docusign"); // overridable per-send; default loaded from /esign/default-provider
  const [signLoading, setSignLoading] = useState(false);
  const [esignStatus, setEsignStatus] = useState<any>(null);
  // Per-tenant e-signature INTENT (from the wizard). Default true → existing
  // "Send for Signature" behavior unchanged; when false the contract is presented
  // as "finalized / pending signature" with a manual Upload-executed-copy path.
  const [esignEnabled, setEsignEnabled] = useState(true);
  const [markingExecuted, setMarkingExecuted] = useState(false);
  const [uploadingExecuted, setUploadingExecuted] = useState(false);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [showAddReviewer, setShowAddReviewer] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [commentAnalysis, setCommentAnalysis] = useState<any>(null);
  const [expandedReviewers, setExpandedReviewers] = useState<Record<string, any>>({});
  const [expandedSends, setExpandedSends] = useState<Record<string, boolean>>({});
  const [analyzingComments, setAnalyzingComments] = useState(false);
  const [sendingReview, setSendingReview] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  // Inline check is broader than share.create (admin/legalops/lawyer); the
  // additional contract_manager + commercial_ops fallback is intentional.
  const canShare = hasPermission(user, 'share.create') || ["admin", "legalops", "lawyer", "contract_manager", "commercial_ops"].includes(user?.role || '');

  const openShareModal = async (versionId: string | null = null) => {
    setShareVersionId(versionId);
    setShareForm({ recipient_name: "", recipient_email: "", recipient_org: "", expires_in_days: "7" });
    setShareUrl("");
    setShowShareModal(true);
    const data = await apiCall(`/share?request_id=${request.id}`);
    if (data?.shares) setExistingShares(data.shares);
  };

  const createShareLink = async () => {
    setShareLoading(true);
    const body: Record<string, any> = { request_id: request.id, ...shareForm, expires_in_days: shareForm.expires_in_days ? parseInt(shareForm.expires_in_days) : null };
    if (shareVersionId) body.version_id = shareVersionId;
    const data = await apiCall("/share", { method: "POST", body: JSON.stringify(body) });
    if (data?.url) {
      setShareUrl(data.url);
      setExistingShares(prev => [data.share, ...prev]);
    }
    setShareLoading(false);
  };

  const revokeShare = async (id: string) => {
    await apiCall(`/share/${id}`, { method: "DELETE" });
    setExistingShares(prev => prev.map((s: any) => s.id === id ? { ...s, is_active: false } : s));
  };

  // Sync status/assignee when req prop updates (without resetting tab)
  useEffect(() => {
    if (req && open) { setNewStatus(request.status || ''); setAssignTo(request.assignedTo || ""); }
  }, [req?.status, req?.assignedTo]);

  // Pull the admin-configured default e-sign provider when the send-for-
  // signature modal opens. Modal-local signProvider state still wins (lets
  // the operator override per-send), but the default seeds the radio so
  // most clicks are zero-input. Endpoint is admin-only; non-admins get a
  // 403 here, which we silently fall through (signProvider stays 'docusign').
  useEffect(() => {
    if (!showSignModal) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await apiCall("/esign/default-provider");
        if (!cancelled && d?.provider && (d.provider === "docusign" || (SIGNNOW_ENABLED && d.provider === "signnow"))) {
          setSignProvider(d.provider);
        }
      } catch { /* non-admin or transient — keep current selection */ }
    })();
    return () => { cancelled = true; };
  }, [showSignModal]);

  // Map API version data to frontend format
  const formatVersionLabel = (raw: string | null | undefined): string => {
    if (!raw || raw === 'current_draft') return 'Initial Draft';
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  };
  const mapVersion = (v: any) => ({ id: v.id, name: v.filename, spName: v.sharepoint_filename || null, date: v.created_at?.split("T")[0], by: v.uploader_name || "Unknown", by_org_type: v.uploader_org_type, num: v.version_number, label: formatVersionLabel(v.label), notes: v.notes, type: v.file_type, locked: v.is_locked, filePath: v.file_path, sharepointUrl: v.sharepoint_url, sharepointFileUrl: v.sharepoint_file_url, draftType: v.draft_type || "internal", counterparty_sends: v.counterparty_sends || [] });
  const mapActivity = (a: any) => ({ date: a.created_at?.split("T")[0], action: a.action, by: a.performer_name || "System", by_org_type: a.performer_org_type });

  // Refresh detail data (versions, activity, reviewers, esign) without full reload
  const refreshDetail = useCallback(async (requestId: string) => {
    if (!requestId) return;
    const [detail, revData, esign] = await Promise.all([
      apiCall(`/requests/${requestId}`),
      apiCall(`/requests/${requestId}/reviewers`),
      apiCall(`/esign/status/${requestId}`),
    ]);
    if (detail) {
      if (detail.versions) setVersions(detail.versions.map(mapVersion));
      if (detail.activity) setActivity(detail.activity.map(mapActivity));
      if (detail.request?.sharepoint_folder_url) setSpFolderUrl(detail.request.sharepoint_folder_url);
      if (detail.request) setRequestDetail({ ...detail.request, repositoryContract: detail.repositoryContract || null });
      setParentInfo(detail.parentInfo || null);
      setChildRequests(detail.childRequests || []);
    }
    if (revData?.reviewers) setReviewers(revData.reviewers);
    if (esign?.envelopes?.length > 0) setEsignStatus(esign.envelopes[0]); else setEsignStatus(null); if (typeof esign?.esign_enabled === "boolean") setEsignEnabled(esign.esign_enabled);
  }, []);

  // Full initial load when modal opens for a new request
  const lastOpenedIdRef = useRef<any>(null);
  useEffect(() => {
    if (!req || !open) return;
    // Only do full load for a new request (not on parent re-renders)
    if (lastOpenedIdRef.current === request.id) return;
    lastOpenedIdRef.current = request.id;
    setNewStatus(request.status || ''); setAssignTo(request.assignedTo || ""); setStatusReason(""); setTab(resolvedInitialTab);
    // Consume initial-nav params on the next macrotask. Deferring is critical:
    // CommunicationsTab is conditionally rendered (mounts only when tab
    // switches to "communications") and needs the initialThreadId prop to
    // still be non-null on its first mount. If we cleared synchronously here,
    // React would re-render with initialThreadId=null BEFORE CommunicationsTab
    // ever mounted, and the deep-link thread would never pre-select.
    // setTimeout(0) lets React flush the tab switch + CommunicationsTab mount
    // first, then the parent clears its pending state.
    setTimeout(() => { onInitialNavConsumed?.(); }, 0);
    setLoadingDetail(true);
    (async () => {
      const [detail, lawyerData, revData, usersData, esign] = await Promise.all([
        apiCall(`/requests/${request.id}`),
        apiCall("/users/lawyers"),
        apiCall(`/requests/${request.id}/reviewers`),
        apiCall("/users"),
        apiCall(`/esign/status/${request.id}`),
      ]);
      if (detail) {
        if (detail.versions) setVersions(detail.versions.map(mapVersion));
        else if (request.versions?.length) setVersions(request.versions);
        if (detail.activity) setActivity(detail.activity.map(mapActivity));
        else if (request.activity?.length) setActivity(request.activity);
        if (detail.request?.sharepoint_folder_url) setSpFolderUrl(detail.request.sharepoint_folder_url);
        if (detail.request) setRequestDetail({ ...detail.request, repositoryContract: detail.repositoryContract || null });
        setParentInfo(detail.parentInfo || null);
        setChildRequests(detail.childRequests || []);
      }
      if (lawyerData?.lawyers) setLawyers(lawyerData.lawyers.map((l: LawyerRow) => ({ id: l.id, name: l.name, email: l.email, specialty: l.lawyer_specialty || "" })));
      if (revData?.reviewers) setReviewers(revData.reviewers);
      if (usersData?.users) setAllUsers(usersData.users);
      if (esign?.envelopes?.length > 0) setEsignStatus(esign.envelopes[0]); else setEsignStatus(null); if (typeof esign?.esign_enabled === "boolean") setEsignEnabled(esign.esign_enabled);
      setLoadingDetail(false);
    })();
  }, [req?.id, open]);

  // Auto-refresh every 8s while modal is open — separate effect so it's never accidentally cleared
  useEffect(() => {
    if (!req?.id || !open) return;
    const detailInterval = setInterval(() => refreshDetail?.(request.id || ''), 8000);
    return () => clearInterval(detailInterval);
  }, [req?.id, open, refreshDetail]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) { lastOpenedIdRef.current = null; }
  }, [open]);

  if (!req) return null;
  const request: RequestRow = req;
  const sc = STATUS_COLORS[request.status || ''] || { c: "", b: "" };
  const lawyer = lawyers.find((l: LawyerRow) => l.id === request.assignedTo) || (request.assignedName ? { name: request.assignedName } : null);
  // RBAC: requests.edit_metadata defaultRoles match this tuple exactly
  const canEdit = hasPermission(user, 'requests.edit_metadata') || ["admin", "lawyer", "legalops", "contract_manager", "commercial_ops"].includes(user?.role || '');

  const handleSave = async () => {
    if (newStatus === "Needs More Information" && !statusReason.trim()) {
      alert("Please provide a reason when requesting more information.");
      return;
    }
    const upd: Record<string, any> = {};
    if (newStatus !== request.status) upd.status = newStatus;
    if (statusReason.trim()) upd.statusReason = statusReason.trim();
    if (assignTo !== request.assignedTo) upd.assignedTo = assignTo || null;
    if (Object.keys(upd).length) onUpdate?.(request.id, upd);
    onClose?.();
  };

  const handleDraftUpload = async () => {
    if (!draftFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", draftFile);
    formData.append("label", draftLabel);
    formData.append("notes", newFileNotes);
    formData.append("counterpartyName", request.counterparty || "");
    formData.append("agreementType", request.type || "");

    const token = localStorage.getItem("kontractai_token");
    try {
      const res = await fetch(`/api/versions/request/${request.id}/upload-draft`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        const labelDisplay = draftLabel.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
        setVersions(prev => [...prev, { id: data.version.id, name: data.version.filename, spName: data.structuredFilename || null, date: today(), by: user!.name, num: data.version.version_number, label: labelDisplay, notes: newFileNotes, type: data.version.file_type, locked: false, filePath: data.version.file_path, sharepointUrl: data.sharepointUrl }]);
        setDraftFile(null);
        setNewFileNotes("");
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Reflect status change from draft label
        if (data.statusUpdate?.newStatus) {
          const statusDisplay = data.statusUpdate.newStatus.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
          // Signal parent to refresh (status already changed server-side by upload handler)
          onUpdate?.(request.id, { _refreshOnly: true, status: statusDisplay });
        }
        // Auto-analyze comments for internal drafts (DOCX only)
        const isDocx = (data.version?.file_type || '').toLowerCase() === 'docx' || (data.version?.filename || '').toLowerCase().endsWith('.docx');
        console.log('[KAI] Upload complete:', { draftLabel, fileType: data.version?.file_type, isDocx, versionId: data.version?.id });
        if (draftLabel === "internal_draft" && data.version?.id && isDocx) {
          console.log('[KAI] Starting comment analysis for version:', data.version.id);
          setAnalyzingComments(true);
          const analysis = await apiCall(`/versions/${data.version.id}/analyze-comments`, { method: 'POST' }).catch((e: any): any => { console.error("Comment analysis failed:", e); return null; });
          setAnalyzingComments(false);
          if (analysis && (analysis.comments?.length > 0 || analysis.matchedUsers?.length > 0)) {
            setCommentAnalysis({ ...analysis, versionId: data.version.id });
            setTimeout(() => { const el = document.querySelector('[data-comment-analysis]'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
          }
          console.log('[KAI] Comment analysis result:', { comments: analysis?.comments?.length || 0, matched: analysis?.matchedUsers?.length || 0 });
        }
        // Wait for SharePoint sync, then re-fetch all detail data
        setSpSyncing(true);
        setTimeout(async () => {
          await refreshDetail?.(request.id || '');
          setSpSyncing(false);
        }, 4000);
      } else {
        const errData = await res.json().catch(() => ({}));
        alert("Upload failed: " + (errData.error || "Unknown error"));
      }
    } catch (err: any) { console.error("Draft upload failed:", err); alert("Upload failed: " + err.message); }
    setUploading(false);
  };

  const handleNote = async () => {
    if (!note.trim()) return;
    await apiCall(`/requests/${request.id}`, { method: "PUT", body: JSON.stringify({}) }); // trigger activity
    // Post note via activity log directly
    const token = localStorage.getItem("kontractai_token");
    // For now, add locally and it will sync on next open
    setActivity(prev => [{ date: today(), action: `Note: ${note}`, by: user!.name }, ...prev]);
    setNote("");
  };

  const sendForSignature = async () => {
    const valid = signSigners.every((s: any) => s.name && s.email);
    if (!valid) return alert("All signers must have a name and email");
    setSignLoading(true);
    const latestVersion = versions.length > 0 ? versions[versions.length - 1] : null;
    const r = await apiCall("/esign/send", {
      method: "POST",
      body: JSON.stringify({
        request_id: request.id,
        version_id: latestVersion?.id || null,
        signers: signSigners,
        ccRecipients: ccRecipients.filter((cc: any) => cc.email?.trim()),
        provider: signProvider,
      }),
    });
    setSignLoading(false);
    if (r?.success) {
      setShowSignModal(false);
      if (r.senderViewUrl) { window.open(r.senderViewUrl, '_blank'); }
      setEsignStatus(r.envelope);
      // SignNow is review-before-send: backend sets pending_signnow_send and
      // the operator finishes the send in SignNow's UI (auto-opened above).
      // Status flips to Sent for Signature when SignNow's invite.create
      // webhook fires.
      const optimisticStatus = signProvider === "signnow" ? "Pending SignNow Send" : "Sent for Signature";
      onUpdate?.(request.id, { status: optimisticStatus });
    } else {
      alert(r?.error || "Failed to send for signature");
    }
  };

  const esignStatusColors: Record<string, { c: string; b: string }> = { sent: { c: T.blu, b: T.bluS }, delivered: { c: T.ylw, b: T.ylwS }, completed: { c: T.grn, b: T.grnS }, declined: { c: T.red, b: T.redS }, voided: { c: T.txD, b: T.bg3 }, created: { c: T.txM, b: T.bg4 } };

  // No permission key exists for in-house spend gating — leaving inline checks.
  const showSpendTab = clientScope.edition === "in_house" && ["admin", "legalops", "lawyer", "contract_manager"].includes(user?.role || '');
  const canWriteSpend = clientScope.edition === "in_house" && ["admin", "legalops"].includes(user?.role || '');
  const tabs = [{ id: "details", label: "Details" }, { id: "versions", label: "Versions", count: versions.length }, { id: "activity", label: "Activity", count: activity.length }, { id: "communications", label: "Communications", count: messageUnreadCount }, ...(showSpendTab ? [{ id: "spend", label: "Spend" }] : []), ...(canEdit ? [{ id: "manage", label: "Manage" }] : [])];

  return (
    <Modal open={open} onClose={() => { setShowShareModal(false); onClose?.(); }} title={request.title} w={760}>
      {(() => {
        // Display-number badge with copy-to-clipboard. Renders only when
        // display_number is set on the row (post-migration: every row).
        // Graceful no-op for missing display_number (very-new rows).
        const dn = formatDisplayNumber(
          request as any,
          'request',
          { edition: clientScope.edition, tenant_prefix: clientScope.tenantPrefix }
        );
        if (!dn) return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, fontWeight: 600, color: T.tx, padding: "4px 10px", backgroundColor: T.bg2, borderRadius: 6, border: `1px solid ${T.brd}` }}>{dn}</span>
            <button
              type="button"
              onClick={() => { try { navigator.clipboard.writeText(dn); } catch (_) { /* ignore */ } }}
              title="Copy display number"
              style={{ background: "transparent", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, padding: "3px 8px", cursor: "pointer", color: T.txM, fontFamily: "inherit" }}
            >Copy</button>
            {user?.role === "admin" && (request as any).status !== "Cancelled" && !(request as any).superseded_by_id && (
              <button
                type="button"
                onClick={async () => {
                  setShowCancelSupersede(true);
                  setCsNewClientId(""); setCsReason(""); setCsPreserve(true);
                  // Fetch clients on demand (admin endpoint /api/clients).
                  const r = await apiCall(`/clients`);
                  const arr = Array.isArray(r) ? r : (r?.clients || []);
                  setCsClients(arr.filter((c: any) => c.is_active && c.id !== (request as any).client_id));
                }}
                title="Reassign this request to a different client"
                style={{ marginLeft: "auto", background: "transparent", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, padding: "3px 10px", cursor: "pointer", color: T.txM, fontFamily: "inherit" }}
              >Reassign client</button>
            )}
          </div>
        );
      })()}
      {(request as any).status === "Cancelled" && (request as any).superseded_by_id && (
        <div style={{ padding: "12px 16px", backgroundColor: "rgba(208,64,64,.06)", border: `1px solid ${T.red}40`, borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 4 }}>Request cancelled and superseded</div>
          <div style={{ fontSize: 12, color: T.txM }}>
            This request was cancelled and replaced. Reason: {(request as any).cancellation_reason || "—"}
          </div>
        </div>
      )}
      {/* Inline check is tighter than requests.update_status (which also includes commercial_ops/lawyer/contract_manager) — preserving existing gate. */}
      {request.status === "Self-Service Draft" && (request.requesterEmail === user?.email || ["admin", "legalops"].includes(user?.role || '')) && (
        <div style={{ padding: "12px 16px", backgroundColor: "rgba(26,138,158,.06)", border: "1px solid rgba(26,138,158,.15)", borderRadius: 8, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#1A8A9E" }}>Self-Service Draft</div>
              <div style={{ fontSize: 12, color: "#5A6278", marginTop: 2 }}>This request was created via self-service. You can upload a counterparty draft or additional versions below, then escalate when ready.</div>
            </div>
            <Btn v="primary" sz="sm" onClick={async () => {
              if (!window.confirm("Escalate this request for CommOps or Legal review? All uploaded versions will be included.")) return;
              const r = await apiCall(`/self-service/escalate/${request.id}`, { method: "POST" });
              if (r) { onUpdate?.(request.id, { _refreshOnly: true, status: r.status?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) || "Pending Assignment" }); onClose?.(); }
              else alert("Escalation failed");
            }}>Escalate for Review</Btn>
          </div>
          {versions.length > 0 && <div style={{ fontSize: 11, color: "#5A6278", marginTop: 6 }}>{versions.length} version{versions.length !== 1 ? "s" : ""} attached. These will carry over when escalated.</div>}
        </div>
      )}
      {deleting && (
        <div style={{ padding: "16px 20px", backgroundColor: "rgba(208,64,64,.06)", border: "1px solid rgba(208,64,64,.15)", borderRadius: 8, marginBottom: 12, textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 4 }}>Deleting request...</div>
          <div style={{ fontSize: 12, color: T.txM }}>{deleteStatus || "Removing versions, files, and SharePoint data..."}</div>
        </div>
      )}
      {canShare && <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 8 }}>{/* Inline check is tighter than requests.delete (which also includes legalops) — preserving existing gate. */}{user?.role === "admin" && <Btn v="ghost" sz="sm" disabled={deleting} onClick={async () => { if (!window.confirm(`Delete "${request.title}"? This removes all versions, files, and SharePoint data. Cannot be undone.`)) return; setDeleting(true); setDeleteStatus("Removing database records and local files..."); const r = await apiCall(`/requests/${request.id}`, { method: "DELETE" }); if (r) { setDeleteStatus("Cleaning up SharePoint files and folders..."); await new Promise(resolve => setTimeout(resolve, 4000)); setDeleteStatus("Deletion complete."); await new Promise(resolve => setTimeout(resolve, 1000)); setDeleting(false); onUpdate?.(request.id, { _deleted: true }); onClose?.(); } else { setDeleteStatus("Deletion failed. Please try again."); setTimeout(() => setDeleting(false), 2000); } }} style={{ color: T.red }}>{deleting ? "Deleting..." : "Delete Request"}</Btn>}{esignEnabled !== false && canEdit && ["Approved for Signature", "Legal Review Completed"].includes(request.status || '') && (!esignStatus || esignStatus.status === "voided" || esignStatus.status === "declined") && <Btn v="primary" sz="sm" onClick={() => setShowSignModal(true)} style={{ backgroundColor: "#5C2D91", borderColor: "#5C2D91" }}>Send for Signature</Btn>}<RefreshBtn onClick={() => refreshDetail?.(request.id || '')} /></div>}
      {esignStatus && (
        <div onClick={() => setShowEsignDetail(true)} style={{ marginBottom: 12, padding: "10px 14px", backgroundColor: T.bg3, borderRadius: 6, border: `1px solid ${T.brd}`, cursor: "pointer", transition: "border-color .15s" }} onMouseEnter={e => e.currentTarget.style.borderColor = T.acc} onMouseLeave={e => e.currentTarget.style.borderColor = T.brd}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>E-Signature</span>
              <Badge c={(esignStatusColors[esignStatus.status || ''] || {}).c || T.txM} b={(esignStatusColors[esignStatus.status || ''] || {}).b || T.bg4}>{esignStatus.status}</Badge>
              {esignStatus.sent_by_name && <span style={{ fontSize: 10, color: T.txD }}>by {esignStatus.sent_by_name}</span>}
              {(() => { const ss = typeof esignStatus.signers === "string" ? JSON.parse(esignStatus.signers) : esignStatus.signers || []; const signed = ss.filter((s: any) => s.status === "completed").length; return <span style={{ fontSize: 10, color: T.txM }}>({signed}/{ss.length} signed)</span>; })()}
            </div>
            <span style={{ fontSize: 11, color: T.acc, fontWeight: 600 }}>View Details</span>
          </div>
        </div>
      )}
      <ESignatureModal open={showEsignDetail} onClose={() => setShowEsignDetail(false)} envelope={esignStatus} />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "details" && (<DetailTabContent req={req} canEdit={canEdit} user={user} lawyer={lawyer} lawyers={lawyers} sc={sc} onUpdate={onUpdate} refreshDetail={refreshDetail} parentInfo={parentInfo} childRequests={childRequests} requestDetail={requestDetail} onNav={(type: string, id: string) => { onClose?.(); if (onNav) onNav?.(type, id); }} />

      )}
      {tab === "versions" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: T.txD }}>Auto-refreshes every 8s</div>
            <RefreshBtn onClick={() => refreshDetail?.(request.id || '')} label="Refresh Now" />
          </div>
          {loadingDetail && versions.length === 0 && <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading versions...</div>}
          {versions.map((v: any) => {
            const spFolder = v.sharepointFileUrl ? v.sharepointFileUrl.substring(0, v.sharepointFileUrl.lastIndexOf('/')) : null;
            const vReviewers = reviewers.filter((r: any) => r.version_id === v.id);
            return (
            <div key={v.id}>
              <div style={{ padding: "12px 16px", backgroundColor: T.bg3, borderRadius: 8, marginBottom: vReviewers.length > 0 ? 0 : 8, border: v.num === Math.max(...versions.map((x: any) => x.num || 0)) ? `2px solid ${T.grn}` : v.locked ? `1px solid ${T.grn}40` : "1px solid transparent", borderBottomLeftRadius: vReviewers.length > 0 ? 0 : 8, borderBottomRightRadius: vReviewers.length > 0 ? 0 : 8 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{v.type === "pdf" ? "📕" : "📄"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.tx, wordBreak: "break-word" }}>{v.spName || v.name}</div>
                    <div style={{ fontSize: 11, color: T.txD, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                      <span>{v.by || "Unknown"}</span>
                      <OrgBadge org_type={v.by_org_type} size="xs" />
                      <span>· {fmtDate(v.date)}</span>
                    </div>
                    {v.notes && <div style={{ fontSize: 11, color: T.txM, marginTop: 4, fontStyle: "italic" }}>{v.notes}</div>}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" as any }}>
                    {(spFolder || v.sharepointUrl) && <a href={spFolder || v.sharepointUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: "#0078D4", textDecoration: "none", fontWeight: 600 }}>SharePoint ↗</a>}
                    {v.filePath && (v.type === "docx" || v.type === "doc") && <span onClick={() => { const spUrl = v.sharepointFileUrl || v.sharepoint_file_url; if (spUrl) { window.location.href = 'ms-word:ofe|u|' + spUrl; } else { openInWord("version", v.id); } }} style={{ fontSize: 10, color: "#2b579a", cursor: "pointer", fontWeight: 600 }}>Open in Word</span>}
                    {v.filePath && <span onClick={() => { downloadDocument("version", v.id, v.name); }} style={{ fontSize: 10, color: T.acc, cursor: "pointer" }}>Download ↓</span>}
                    {v.draftType === "company" && v.num === Math.max(...versions.filter((x: any) => x.draftType === "company").map((x: any) => x.num || 0)) && (hasPermission(user, 'comms.send_to_counterparty') || ["admin","lawyer","legalops","commercial_ops","contract_manager","requester"].includes(user?.role || "")) && <span onClick={() => setShowSendModal(true)} style={{ fontSize: 10, color: T.acc, cursor: "pointer", fontWeight: 600 }}>Send to counterparty</span>}
                    {v.draftType !== "company" && v.draftType !== "final" && v.label !== "company_draft" && v.label !== "final_draft" && (hasPermission(user, 'versions.promote_to_company_draft') || ["admin","lawyer","legalops","contract_manager","commercial_ops"].includes(user?.role || "")) && <span onClick={async (e: React.MouseEvent) => { e.stopPropagation(); if (!window.confirm("Promote v" + v.num + " (" + (v.label || v.draftType) + ") to company draft? This makes it eligible to send to the counterparty and transitions the request status.")) return; const r = await apiCall("/versions/" + v.id + "/promote-to-company-draft", { method: "POST" }); if (r && r.ok) { void refreshDetail?.(request.id || ""); } else { alert((r && r.error) || "Failed to promote draft"); } }} style={{ fontSize: 10, color: "#0078D4", cursor: "pointer", fontWeight: 600 }}>Promote to company draft</span>}
                    {canEdit && !v.locked && <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Delete v${v.num} "${v.spName || v.name}"?`)) return; const r = await apiCall(`/versions/${v.id}`, { method: 'DELETE' }); if (r?.message) setVersions(prev => prev.filter((x: any) => x.id !== v.id)); else alert(r?.error || 'Delete failed'); }} style={{ fontSize: 10, color: T.red, cursor: "pointer", fontWeight: 600, background: "none", border: "none", fontFamily: "inherit" }}>Delete</button>}
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {v.draftType === "company" && <Badge c="#0078D4" b="#0078D415">Company</Badge>}
                    {(v.draftType === "counterparty" || v.draftType === "client_facing") && <Badge c={T.orn} b={T.ornS}>Counterparty</Badge>}
                    {v.draftType === "final" && <Badge c={T.grn} b={T.grnS}>Final</Badge>}
                    <Badge c={v.num === Math.max(...versions.map((x: any) => x.num || 0)) ? T.acc : T.txM} b={v.num === Math.max(...versions.map((x: any) => x.num || 0)) ? T.accS : T.bg4}>{v.label || "Draft"}</Badge>
                    <Badge c={T.txM} b={T.bg4}>v{v.num}</Badge>
                    {v.locked && <Badge c={T.grn} b={T.grnS}>🔒</Badge>}
                  </div>
                </div>
              </div>
              {v.counterparty_sends && v.counterparty_sends.length > 0 && (
                <div style={{ padding: "4px 12px 6px 36px", backgroundColor: T.bg3, borderRadius: "0 0 5px 5px", marginTop: -1, marginBottom: 4 }}>
                  {v.counterparty_sends.length === 1 ? (
                    <div style={{ fontSize: 10, color: T.txM }}>
                      {"\u2197 Sent to " + v.counterparty_sends[0].counterparty_email + " on " + new Date(v.counterparty_sends[0].sent_at).toLocaleDateString() + " at " + new Date(v.counterparty_sends[0].sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + " by " + (v.counterparty_sends[0].sent_by_name || "Unknown")}
                    </div>
                  ) : (
                    <>
                      <button onClick={() => setExpandedSends((prev) => ({ ...prev, [v.id]: !prev[v.id] }))} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: T.txM, fontWeight: 600, padding: 0, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ transform: expandedSends[v.id] ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block", fontSize: 8 }}>{"\u25B6"}</span>
                        {"Sent " + v.counterparty_sends.length + " times"}
                      </button>
                      {expandedSends[v.id] && (
                        <div style={{ marginLeft: 12, marginTop: 2 }}>
                          {v.counterparty_sends.map((s: any) => (
                            <div key={s.id} style={{ fontSize: 10, color: T.txD, padding: "1px 0" }}>
                              {"\u2197 Sent to " + s.counterparty_email + " on " + new Date(s.sent_at).toLocaleDateString() + " at " + new Date(s.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + " by " + (s.sent_by_name || "Unknown")}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {vReviewers.length > 0 && (
                <div style={{ marginLeft: 20, marginBottom: 8, borderLeft: `2px solid ${T.brd}`, paddingLeft: 12 }}>
                  <button onClick={() => setExpandedReviewers((prev: any) => ({ ...prev, [v.id]: !prev[v.id] }))}
                    style={{ fontSize: 10, color: T.txD, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: "4px 0", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ transform: expandedReviewers[v.id] ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", display: "inline-block" }}>▶</span>
                    Reviewer Feedback ({vReviewers.filter(r => r.review_status === 'feedback_received').length}/{vReviewers.length} received)
                  </button>
                  {expandedReviewers[v.id] && (
                    <div style={{ marginTop: 6 }}>
                      {vReviewers.map(r => {
                        const assignedList = (() => {
                          try {
                            const raw = typeof r.comments_assigned === 'string' ? JSON.parse(r.comments_assigned) : r.comments_assigned;
                            return Array.isArray(raw) ? raw : [];
                          } catch { return []; }
                        })();
                        const fmtDT = (iso: any) => {
                          if (!iso) return '';
                          const d = new Date(iso);
                          return isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                        };
                        return (
                        <div key={r.id} style={{ padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, marginBottom: 4, backgroundColor: T.bg }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: T.tx }}>{r.reviewer_name}</span>
                              <OrgBadge org_type={r.reviewer_org_type} size="xs" />
                              {!r.user_id && <Badge c={T.txD} b={T.bg4}>External</Badge>}
                            </span>
                            <Badge c={r.review_status === 'feedback_received' ? T.grn : T.orn} b={r.review_status === 'feedback_received' ? T.grnS : T.ornS}>
                              {r.review_status === 'feedback_received' ? 'Received' : 'Pending'}
                            </Badge>
                          </div>
                          {r.email_sent_at && (
                            <div style={{ fontSize: 9, color: T.txD, marginTop: 4 }}>Escalated: {fmtDT(r.email_sent_at)}</div>
                          )}
                          {assignedList.length > 0 && (
                            <div style={{ marginTop: 4, padding: "6px 8px", backgroundColor: T.bg3, borderRadius: 4, borderLeft: `3px solid ${T.acc}` }}>
                              <div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>Assigned items</div>
                              {assignedList.map((c: any, i: number) => (
                                <div key={i} style={{ fontSize: 11, color: T.tx, lineHeight: 1.4, marginBottom: 2 }}>
                                  <strong>{c.section || 'General'}:</strong> {c.summary || c.actionRequired}
                                  {c.actionRequired && c.summary && (
                                    <span style={{ color: T.txM }}> — {c.actionRequired}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {r.feedback && (
                            <div style={{ marginTop: 6, padding: 8, backgroundColor: T.bg3, borderRadius: 4, borderLeft: `3px solid ${T.grn}` }}>
                              <div style={{ fontSize: 9, color: T.txD }}>Response{r.feedback_at ? ` · ${fmtDT(r.feedback_at)}` : ''}</div>
                              <div style={{ fontSize: 11, color: T.tx, lineHeight: 1.5, whiteSpace: "pre-line" }}>{r.feedback}</div>
                            </div>
                          )}
                          {r.user_id === user?.id && r.review_status === 'request_sent' && (
                            <div style={{ marginTop: 6, padding: 8, border: `1px solid ${T.acc}`, borderRadius: 5 }}>
                              <textarea id={`feedback-${r.id}`} placeholder="Enter your feedback..." style={{ width: "100%", minHeight: 60, padding: "6px 8px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                              <Btn v="primary" sz="sm" onClick={async () => {
                                const fb = (document.getElementById(`feedback-${r.id}`) as HTMLInputElement | null)?.value;
                                if (!fb?.trim()) return alert('Please enter feedback');
                                const result = await apiCall(`/requests/${request.id}/reviewer-feedback`, { method: 'POST', body: JSON.stringify({ feedback: fb.trim() }) });
                                if (result?.success) setReviewers(prev => prev.map((rev: any) => rev.user_id === user!.id ? { ...rev, review_status: 'feedback_received', feedback: fb.trim(), feedback_at: new Date().toISOString() } : rev));
                                else alert(result?.error || 'Failed');
                              }} style={{ marginTop: 4 }}>Submit Feedback</Btn>
                            </div>
                          )}
                          {canEdit && r.review_status !== 'feedback_received' && (
                            <button onClick={async () => { await apiCall(`/requests/${request.id}/reviewers/${r.id}`, { method: 'DELETE' }); setReviewers(prev => prev.filter((x: any) => x.id !== r.id)); }}
                              style={{ fontSize: 9, color: T.red, background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>Remove</button>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
          {/* General reviewers (no version linked) */}
          {reviewers.filter((r: any) => !r.version_id).length > 0 && (
            <div style={{ marginTop: 14, padding: 12, backgroundColor: T.bg3, borderRadius: 6 }}>
              <div style={{ fontSize: 9, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>REVIEWERS</div>
              {reviewers.filter((r: any) => !r.version_id).map(r => (
                <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
                  <span style={{ fontSize: 12, color: T.tx, display: "inline-flex", alignItems: "center", gap: 5 }}>{r.reviewer_name} <OrgBadge org_type={r.reviewer_org_type} size="xs" /> ({r.role?.replace(/_/g, ' ')})</span>
                  {canEdit && <button onClick={async () => { await apiCall(`/requests/${request.id}/reviewers/${r.id}`, { method: 'DELETE' }); setReviewers(prev => prev.filter((x: any) => x.id !== r.id)); }} style={{ fontSize: 9, color: T.red, background: "none", border: "none", cursor: "pointer" }}>Remove</button>}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <div style={{ marginTop: reviewers.length > 0 ? 8 : 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn v="secondary" sz="sm" onClick={() => setShowAddReviewer(!showAddReviewer)}>+ Add Reviewer</Btn>
              {onNavigateToDrafting && <button onClick={() => { onClose?.(); onNavigateToDrafting?.(request.id); }} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 600, border: `2px solid ${T.acc}`, borderRadius: 6, background: T.accS, color: T.acc, cursor: "pointer", fontFamily: "inherit" }}>Open in Drafting</button>}
            </div>
          )}
          {showAddReviewer && canEdit && (
            <div style={{ marginTop: 8 }}>
              <select onChange={async (e) => {
                if (!e.target.value) return;
                const selectedUserId = e.target.value;
                const r = await apiCall(`/requests/${request.id}/reviewers`, { method: 'POST', body: JSON.stringify({ user_id: selectedUserId, role: 'business_reviewer' }) });
                if (r?.reviewer) {
                  const u = allUsers.find((u: UserRow) => u.id === selectedUserId);
                  setReviewers(prev => [...prev, { ...r.reviewer, reviewer_name: u?.name || 'Unknown', role: 'business_reviewer' }]);
                  setShowAddReviewer(false);
                } else alert(r?.error || 'Failed');
                e.target.value = '';
              }} style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 5, fontSize: 12, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit" }}>
                <option value="">Select reviewer...</option>
                {allUsers.filter((u: UserRow) => !reviewers.some((r: any) => r.user_id === u.id)).map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
            </div>
          )}
          {/* Inline check is tighter than requests.update_status (which also includes lawyer/contract_manager) — preserving existing gate. */}
          {(request.status === "Counterparty Draft Ready" || request.status === "counterparty_draft_ready") && (user?.email === request.requesterEmail || ["admin", "legalops", "commercial_ops"].includes(user?.role || '')) && (
            <div style={{ marginTop: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#166534" }}>Draft sent to counterparty?</div>
                <div style={{ fontSize: 10, color: "#4ade80" }}>Update the status once the counterparty has received the draft</div>
              </div>
              <Btn v="primary" sz="sm" onClick={async () => {
                const r = await apiCall(`/requests/${request.id}/mark-sent`, { method: 'POST' });
                if (r?.success) { onUpdate?.(request.id, { _refreshOnly: true, status: "Counterparty Review Pending" }); }
                else { alert(r?.error || 'Failed to update status'); }
              }} style={{ background: "#16a34a", whiteSpace: "nowrap" }}>Mark as Sent</Btn>
            </div>
          )}
          {(request.status === "Approved for Signature" || request.status === "approved_for_signature") && canEdit && !esignStatus && esignEnabled !== false && (
            <div style={{ marginTop: 10 }}>
              <Btn v="primary" sz="sm" onClick={() => setShowSignModal(true)} style={{ backgroundColor: "#5C2D91", borderColor: "#5C2D91" }}>Send for Signature</Btn>
            </div>
          )}
          {/* E-signature-ABSENT customer: finalized contracts are "pending signature";
              an admin/legalops uploads the fully executed copy and marks it executed,
              converging on the same AI extraction queue (no DocuSign). */}
          {(request.status === "Approved for Signature" || request.status === "approved_for_signature") && esignEnabled === false && (
            <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg3 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Finalized — pending signature</div>
              <div style={{ fontSize: 12, color: T.txM, marginBottom: 10 }}>
                This customer does not use e-signature. Once the agreement is signed off-platform, upload the fully
                executed copy and mark it executed — it then enters the AI extraction queue.
              </div>
              {["admin", "legalops"].includes(user?.role || "") ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ display: "inline-block" }}>
                    <input type="file" style={{ display: "none" }} disabled={uploadingExecuted} onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setUploadingExecuted(true);
                      const fd = new FormData(); fd.append("file", f); fd.append("label", "execution_copy");
                      const r = await apiCall(`/versions/request/${request.id}/upload-draft`, { method: "POST", body: fd });
                      setUploadingExecuted(false);
                      if (r) { refreshDetail?.(request.id || ''); } else { alert("Upload failed."); }
                      (e.target as HTMLInputElement).value = "";
                    }} />
                    <span style={{ display: "inline-block", padding: "6px 14px", borderRadius: 6, border: `1px solid ${T.acc}`, color: T.acc, fontSize: 12, fontWeight: 600, cursor: uploadingExecuted ? "default" : "pointer" }}>{uploadingExecuted ? "Uploading…" : "Upload executed copy"}</span>
                  </label>
                  <Btn v="success" sz="sm" disabled={markingExecuted} onClick={async () => {
                    if (!window.confirm("Mark this contract executed? The uploaded executed copy will be queued for AI extraction.")) return;
                    setMarkingExecuted(true);
                    const r = await apiCallEx(`/versions/request/${request.id}/mark-executed`, { method: "POST" });
                    setMarkingExecuted(false);
                    if (r.ok && r.data?.success) { onUpdate?.(request.id, { _refreshOnly: true, status: "Fully Executed" }); refreshDetail?.(request.id || ''); }
                    else { alert(r.data?.message || r.data?.error || "Could not mark executed."); }
                  }}>{markingExecuted ? "Marking…" : "Mark executed"}</Btn>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.txD }}>An admin or legal-ops user uploads the executed copy and marks it executed.</div>
              )}
            </div>
          )}
          {analyzingComments && (
            <div style={{ marginTop: 12, padding: 12, backgroundColor: T.bg3, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 12, height: 12, border: `2px solid ${T.acc}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              <span style={{ fontSize: 12, color: T.txM }}>Scanning document for comments and @mentions...</span>
            </div>
          )}
          {commentAnalysis && (
            <div data-comment-analysis style={{ marginTop: 12, padding: 16, border: `1px solid ${T.brd}`, borderRadius: 8, backgroundColor: T.bg }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Review Request ({commentAnalysis.comments?.length || 0} comments with @mentions{commentAnalysis.allCommentCount ? ` of ${commentAnalysis.allCommentCount} total` : ''})</div>
              {commentAnalysis.carriedOverCount > 0 && !commentAnalysis.includeAll && (
                <div style={{ marginBottom: 10, padding: "6px 10px", fontSize: 11, color: T.txM, backgroundColor: T.ylwS, borderRadius: 4, border: `1px solid ${T.ylw}` }}>
                  {commentAnalysis.carriedOverCount} older comment{commentAnalysis.carriedOverCount === 1 ? '' : 's'} from v{commentAnalysis.priorVersionNumber} hidden — they were already present in the prior draft.
                  {" "}
                  <a
                    href="#"
                    style={{ color: T.acc, textDecoration: "underline" }}
                    onClick={async (e) => {
                      e.preventDefault();
                      if (!commentAnalysis.versionId) return;
                      const again = await apiCall(`/versions/${commentAnalysis.versionId}/analyze-comments`, {
                        method: 'POST',
                        body: JSON.stringify({ includeAll: true }),
                      }).catch(() => null);
                      if (again) setCommentAnalysis({ ...again, versionId: commentAnalysis.versionId });
                    }}
                  >
                    Include them anyway
                  </a>
                </div>
              )}
              {commentAnalysis.comments?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Comments Found ({commentAnalysis.comments.length})</div>
                  {(commentAnalysis.summaries?.length > 0 ? commentAnalysis.summaries : commentAnalysis.comments).map((s: any, i: number) => (
                    <div key={i} style={{ padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 5, marginBottom: 4, fontSize: 11, color: T.tx }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{s.section || s.author || `Comment ${i + 1}`}</div>
                      <div>{s.summary || s.text}</div>
                      {s.actionRequired && <div style={{ fontSize: 10, color: T.orn, marginTop: 2 }}>Action: {s.actionRequired}</div>}
                    </div>
                  ))}
                </div>
              )}
              {commentAnalysis.matchedUsers?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Matched Reviewers</div>
                  {commentAnalysis.matchedUsers.map((m: any, i: number) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12, color: T.tx }}>
                      <span>{m.user!.name} ({m.user!.email})</span>
                      <Badge c={T.grn} b={T.grnS}>Matched</Badge>
                    </div>
                  ))}
                </div>
              )}
              {commentAnalysis.unmatchedMentions?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", marginBottom: 4 }}>Unmatched Mentions</div>
                  {commentAnalysis.unmatchedMentions.map((name: string, i: number) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, color: T.txD }}>
                      <span>{name}</span>
                      <input placeholder="Enter email..." style={{ flex: 1, padding: "4px 8px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, backgroundColor: T.bg3, color: T.tx, fontFamily: "inherit" }}
                        onChange={e => {
                          setCommentAnalysis((prev: any) => {
                            const updated = { ...prev, unmatchedEmails: [...(prev.unmatchedEmails || []).filter((x: any) => x.name !== name)] };
                            if (e.target.value) updated.unmatchedEmails.push({ name, email: e.target.value });
                            return updated;
                          });
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Btn v="primary" sz="sm" onClick={async () => {
                  setSendingReview(true);
                  const r = await apiCall(`/versions/${commentAnalysis.versionId}/send-review-request`, {
                    method: 'POST',
                    body: JSON.stringify({
                      reviewers: commentAnalysis.matchedUsers?.map((m: any) => ({ userId: m.user!.id, name: m.user!.name, email: m.user!.email })) || [],
                      summaries: commentAnalysis.summaries || [],
                      unmatchedEmails: commentAnalysis.unmatchedEmails || [],
                      originalComments: commentAnalysis.comments || [],
                    }),
                  });
                  setSendingReview(false);
                  if (r?.sent > 0) {
                    setCommentAnalysis(null);
                    const revData = await apiCall(`/requests/${request.id}/reviewers`);
                    if (revData?.reviewers) setReviewers(revData.reviewers);
                  }
                  else { alert('Failed to send review requests.'); }
                }} disabled={sendingReview}>
                  {sendingReview ? "Sending..." : `Send Review Request (${(commentAnalysis.matchedUsers?.length || 0) + (commentAnalysis.unmatchedEmails?.length || 0)} recipients)`}
                </Btn>
                <Btn v="secondary" sz="sm" onClick={() => setCommentAnalysis(null)}>Dismiss</Btn>
              </div>
            </div>
          )}
          {(hasPermission(user, 'requests.upload_drafts') || canEdit || user?.role === "requester") && <div style={{ marginTop: 14, padding: 16, border: `1px solid ${T.brd}`, borderRadius: 8, backgroundColor: T.bg }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.tx, marginBottom: 10 }}>Upload New Version</div>
            {(hasPermission(user, 'requests.edit_metadata') || ["admin", "lawyer", "legalops", "contract_manager", "commercial_ops"].includes(user?.role || '')) ? (
              <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                {[
                  { id: "internal_draft", label: "Internal Draft", color: T.txD },
                  { id: "company_draft", label: "Company Draft", color: "#0078D4" },
                  { id: "counterparty_draft", label: "Counterparty Draft", color: T.orn },
                  { id: "final_draft", label: "Final Draft", color: T.grn },
                ].map(opt => (
                  <button key={opt.id} onClick={() => setDraftLabel(opt.id)} style={{ flex: 1, minWidth: 100, padding: "8px 4px", fontSize: 10, fontWeight: 600, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", border: `2px solid ${draftLabel === opt.id ? opt.color : T.brd}`, backgroundColor: draftLabel === opt.id ? (opt.color + "15") : "transparent", color: draftLabel === opt.id ? opt.color : T.txD }}>{opt.label}</button>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: T.txD, marginBottom: 14, padding: "8px 12px", backgroundColor: T.bg3, borderRadius: 6 }}>
                Uploading as: <strong style={{ color: T.orn }}>Counterparty Draft</strong>
              </div>
            )}
            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setDraftFile(f); }} onClick={() => fileInputRef.current?.click()} style={{ padding: 20, borderRadius: 8, textAlign: "center", cursor: "pointer", border: `2px dashed ${dragOver ? T.acc : draftFile ? T.grn : T.brd}`, backgroundColor: dragOver ? T.accS : draftFile ? T.grnS : T.bg3, transition: "all 0.15s", marginBottom: 12 }}>
              <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc" onChange={e => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) setDraftFile(f); }} style={{ display: "none" }} />
              {draftFile ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.grn, marginBottom: 4 }}>{draftFile.name}</div>
                  <div style={{ fontSize: 10, color: T.txM }}>{(draftFile.size / 1024).toFixed(0)} KB</div>
                  <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDraftFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} style={{ fontSize: 10, color: T.red, background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>Remove</button>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 20, marginBottom: 4, opacity: 0.4 }}>+</div>
                  <div style={{ fontSize: 12, color: T.txM }}>Drop file here or <span style={{ color: T.acc, fontWeight: 600 }}>browse</span></div>
                  <div style={{ fontSize: 10, color: T.txD, marginTop: 4 }}>PDF, DOCX, or DOC</div>
                </div>
              )}
            </div>
            <Inp label="Notes" value={newFileNotes} onChange={setNewFileNotes} small />
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <Btn v="primary" onClick={() => { handleDraftUpload(); }} disabled={!draftFile || uploading}>{uploading ? "Uploading to SharePoint..." : "Upload & Save to SharePoint"}</Btn>
            </div>
            {uploading && <div style={{ fontSize: 11, color: T.acc, marginTop: 6, textAlign: "center" }}>Saving version and syncing to SharePoint...</div>}
          </div>}
        </div>
      )}
      {tab === "activity" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: T.txD }}>Auto-refreshes every 8s</div>
            <RefreshBtn onClick={() => refreshDetail?.(request.id || '')} label="Refresh Now" />
          </div>
          {canEdit && <div style={{ display: "flex", gap: 8, marginBottom: 14 }}><input value={note} onChange={e => setNote(e.target.value)} placeholder="Add note..." onKeyDown={e => e.key === "Enter" && handleNote()} style={{ flex: 1, padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit" }} /><Btn v="secondary" sz="sm" onClick={() => { handleNote(); }} disabled={!note.trim()}>Add</Btn></div>}
          {loadingDetail && activity.length === 0 && <div style={{ textAlign: "center", padding: 20, color: T.txM, fontSize: 12 }}>Loading activity...</div>}
          <div style={{ borderLeft: `2px solid ${T.brd}`, marginLeft: 8, paddingLeft: 16 }}>
            {activity.map((a: any, i: number) => (
              <div key={i} style={{ marginBottom: 12, position: "relative" }}><div style={{ position: "absolute", left: -21, top: 4, width: 8, height: 8, borderRadius: 4, backgroundColor: a.action?.startsWith("Note:") ? T.ylw : T.acc }} /><div style={{ fontSize: 10, color: T.txD, display: "flex", alignItems: "center", gap: 5 }}>{fmtDate(a.date)} · <span>{a.by}</span><OrgBadge org_type={a.by_org_type} size="xs" /></div><div style={{ fontSize: 12, color: T.tx, marginTop: 2 }}>{a.action}</div></div>
            ))}
          </div>
        </div>
      )}
      {tab === "communications" && req?.id && (
        <CommunicationsTab
          requestId={req.id}
          currentUser={user}
          onUnreadCountChange={setMessageUnreadCount}
          onNavigateToVersions={() => setTab("versions")}
          initialThreadId={initialThreadId}
        />
      )}
      {tab === "spend" && showSpendTab && req?.id && (
        <SpendTabContent requestId={req.id} canWrite={canWriteSpend} />
      )}
      {tab === "manage" && canEdit && (
        <div>
          {/* Online Terms URL (shown when request is linked to online terms) */}
          {requestDetail?.parent_is_online_terms && (
            <div style={{ padding: 12, backgroundColor: T.cynS, border: `1px solid ${T.cyn}30`, borderRadius: 6, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.cyn, marginBottom: 4 }}>Online Terms (Parent Agreement)</div>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 8 }}>The online terms URL must be provided before this request can be approved for signature.</div>
              {requestDetail.online_terms_url ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <a href={requestDetail.online_terms_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: T.cyn, fontWeight: 600, wordBreak: "break-all" }}>{requestDetail.online_terms_url}</a>
                  <button onClick={() => { const url = prompt("Update Online Terms URL:", requestDetail.online_terms_url); if (url !== null) apiCall(`/requests/${request.id}`, { method: "PUT", body: JSON.stringify({ online_terms_url: url }) }).then(() => refreshDetail?.(request.id || '')); }} style={{ fontSize: 10, color: T.acc, background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Edit</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <input id="onlineTermsUrlInput" placeholder="https://..." style={{ flex: 1, padding: "6px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit" }} />
                  <Btn v="primary" sz="sm" onClick={async () => { const url = (document.getElementById("onlineTermsUrlInput") as HTMLInputElement | null)?.value; if (!url?.trim()) return; await apiCall(`/requests/${request.id}`, { method: "PUT", body: JSON.stringify({ online_terms_url: url.trim() }) }); refreshDetail?.(request.id || ''); }}>Save URL</Btn>
                </div>
              )}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Sel label="Status" value={newStatus} onChange={v => { setNewStatus(v); if (v !== "Needs More Information") setStatusReason(""); }} options={ALL_STATUSES} />
            {(hasPermission(user, 'requests.assign') || ["admin", "legalops"].includes(user?.role || '')) && <Sel label="Assign" value={assignTo} onChange={setAssignTo} options={lawyers.map((l: LawyerRow) => ({ value: l.id, label: `${l.name} (${l.specialty})` }))} placeholder="Unassigned" />}
          </div>
          {newStatus === "Needs More Information" && (
            <div style={{ marginTop: 10 }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 5, letterSpacing: ".04em", textTransform: "uppercase" }}>Reason (required)</label>
              <textarea value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="Explain what additional information is needed..." rows={3} style={{ width: "100%", padding: "8px 10px", borderRadius: 5, border: `1px solid ${statusReason.trim() ? T.brd : T.red}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
            {canEscalate && <Btn v="secondary" onClick={() => setHandoffDirection("firm")}>Escalate to outside counsel</Btn>}
            {canReturn && <Btn v="secondary" onClick={() => setHandoffDirection("in_house")}>Return to in-house</Btn>}
            <Btn v="secondary" onClick={onClose}>Cancel</Btn>
            <Btn v="primary" onClick={() => { handleSave(); }}>Save</Btn>
          </div>
        </div>
      )}
      {handoffDirection && (
        <HandoffModal
          open={!!handoffDirection}
          direction={handoffDirection}
          requestId={request.id || ""}
          onClose={() => setHandoffDirection(null)}
          onSuccess={() => { setHandoffDirection(null); onUpdate?.(request.id, { _refreshOnly: true }); }}
        />
      )}
      {showSendModal && (
        <SendToCounterpartyModal
          requestId={request.id || ""}
          onClose={() => setShowSendModal(false)}
          onSuccess={(emails: string[]) => { setShowSendModal(false); }}
          onError={(msg: string) => { alert(msg); }}
        />
      )}
      {showShareModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowShareModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: T.bg2, borderRadius: 10, border: `1px solid ${T.brd}`, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Share Draft{shareVersionId ? " (Specific Version)" : ""}</div>
            <div style={{ fontSize: 11, color: T.txM, marginBottom: 16 }}>{request.title}</div>
            {shareUrl ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.grn, marginBottom: 8 }}>Link created!</div>
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <input readOnly value={shareUrl} style={{ flex: 1, padding: "8px 10px", borderRadius: 5, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, fontFamily: "inherit" }} />
                  <Btn v="primary" sz="sm" onClick={() => navigator.clipboard.writeText(shareUrl)}>Copy</Btn>
                </div>
                <Btn v="secondary" sz="sm" onClick={() => { setShareUrl(""); setShareForm({ recipient_name: "", recipient_email: "", recipient_org: "", expires_in_days: "7" }); }}>Create Another</Btn>
              </div>
            ) : (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <Inp label="Recipient Name" value={shareForm.recipient_name} onChange={v => setShareForm((p: any) => ({ ...p, recipient_name: v }))} small />
                  <Inp label="Recipient Email" value={shareForm.recipient_email} onChange={v => setShareForm((p: any) => ({ ...p, recipient_email: v }))} small />
                  <Inp label="Organization" value={shareForm.recipient_org} onChange={v => setShareForm((p: any) => ({ ...p, recipient_org: v }))} small />
                  <Sel label="Expires In" value={shareForm.expires_in_days} onChange={v => setShareForm((p: any) => ({ ...p, expires_in_days: v }))} options={[{ value: "7", label: "7 days" }, { value: "14", label: "14 days" }, { value: "30", label: "30 days" }, { value: "90", label: "90 days" }, { value: "", label: "Never" }]} />
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Btn v="secondary" sz="sm" onClick={() => setShowShareModal(false)}>Cancel</Btn>
                  <Btn v="primary" sz="sm" onClick={() => { createShareLink(); }} disabled={shareLoading}>{shareLoading ? "Creating..." : "Create Link"}</Btn>
                </div>
              </div>
            )}
            {existingShares.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `1px solid ${T.brd}`, paddingTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.txD, textTransform: "uppercase", marginBottom: 8 }}>Existing Links</div>
                {existingShares.map((s: any) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${T.brdSubtle}`, fontSize: 11 }}>
                    <span style={{ flex: 1, color: s.is_active ? T.tx : T.txD }}>{s.recipient_name || s.recipient_email || "Anonymous"}{s.version_number ? ` (v${s.version_number})` : ""}</span>
                    <span style={{ color: T.txD }}>{s.view_count || 0} views</span>
                    {s.is_active ? <span onClick={() => revokeShare(s.id)} style={{ color: T.red, cursor: "pointer", fontWeight: 600 }}>Revoke</span> : <Badge c={T.txD} b={T.bg4}>Revoked</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {showSignModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowSignModal(false)}>
          <div style={{ backgroundColor: T.bg2, borderRadius: 8, padding: 24, width: 520, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(0,0,0,.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Send for E-Signature</div>
            <div style={{ fontSize: 11, color: T.txM, marginBottom: 16 }}>{request.title}</div>
            {/* Provider selector — defaults to admin-configured default; can be overridden per-send. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "8px 10px", backgroundColor: T.bg3, borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.txD, letterSpacing: ".04em", textTransform: "uppercase", marginRight: 4 }}>Provider</div>
              {SIGNNOW_ENABLED ? [
                { v: "docusign", l: "DocuSign" },
                { v: "signnow", l: "SignNow" },
              ].map(o => (
                <button key={o.v} onClick={() => setSignProvider(o.v as "docusign" | "signnow")} style={{
                  padding: "4px 12px", borderRadius: 4,
                  border: `1px solid ${signProvider === o.v ? T.acc : T.brd}`,
                  backgroundColor: signProvider === o.v ? T.accS : "transparent",
                  color: signProvider === o.v ? T.acc : T.txM,
                  fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                }}>{o.l}</button>
              )) : (
                <div style={{ fontSize: 11, fontWeight: 600, color: T.tx }}>DocuSign</div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>Signers</div>
              <div style={{ display: "flex", gap: 4 }}>
                {[{ v: "parallel", l: "All at once" }, { v: "sequential", l: "In order" }].map(o => (
                  <button key={o.v} onClick={() => { setSigningOrder(o.v); if (o.v === "parallel") setSignSigners(prev => prev.map((s: any) => ({ ...s, order: 1 }))); else setSignSigners(prev => prev.map((s: any, i: number) => ({ ...s, order: i + 1 }))); }} style={{ padding: "4px 12px", borderRadius: 4, border: `1px solid ${signingOrder === o.v ? T.acc : T.brd}`, backgroundColor: signingOrder === o.v ? T.accS : "transparent", color: signingOrder === o.v ? T.acc : T.txM, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{o.l}</button>
                ))}
              </div>
            </div>
            {signSigners.map((s: any, i: number) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: signingOrder === "sequential" ? "auto 1fr 1fr auto auto" : "1fr 1fr auto auto", gap: 8, marginBottom: 8, alignItems: "end" }}>
                {signingOrder === "sequential" && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 36, borderRadius: 6, backgroundColor: T.bg3, color: T.txM, fontSize: 13, fontWeight: 700 }}>{i + 1}</div>}
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 3, letterSpacing: ".04em", textTransform: "uppercase" }}>Name</label>
                  <input value={s.name} onChange={e => { const arr = [...signSigners]; arr[i] = { ...arr[i], name: e.target.value }; setSignSigners(arr); }} placeholder="Full Name" style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 3, letterSpacing: ".04em", textTransform: "uppercase" }}>Email</label>
                  <input value={s.email} onChange={e => { const arr = [...signSigners]; arr[i] = { ...arr[i], email: e.target.value }; setSignSigners(arr); }} placeholder="email@example.com" style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 3, letterSpacing: ".04em", textTransform: "uppercase" }}>Role</label>
                  <select value={s.role} onChange={e => { const arr = [...signSigners]; arr[i] = { ...arr[i], role: e.target.value }; setSignSigners(arr); }} style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                    <option value="Counterparty">Counterparty</option>
                    <option value="Company">Company</option>
                    <option value="Witness">Witness</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  {signSigners.length > 1 && <button onClick={() => setSignSigners(signSigners.filter((_: any, j: number) => j !== i))} style={{ padding: "8px 10px", border: "none", backgroundColor: T.redS, color: T.red, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>X</button>}
                </div>
              </div>
            ))}
            <Btn v="ghost" sz="sm" onClick={() => setSignSigners([...signSigners, { name: "", email: "", role: "Other", order: signingOrder === "sequential" ? signSigners.length + 1 : 1 }])} style={{ marginBottom: 16 }}>+ Add Signer</Btn>
            {signingOrder === "parallel" && <div style={{ fontSize: 10, color: T.txM, marginBottom: 12 }}>All signers will receive the signing request simultaneously.</div>}
            {signingOrder === "sequential" && <div style={{ fontSize: 10, color: T.txM, marginBottom: 12 }}>Each signer receives the request only after the previous signer completes. Note: sequential delivery may be delayed in the DocuSign demo environment.</div>}

            {/* CC Recipients */}
            <div style={{ borderTop: `1px solid ${T.brd}`, paddingTop: 12, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>CC Recipients</div>
                <Btn v="ghost" sz="sm" onClick={() => setCcRecipients([...ccRecipients, { name: "", email: "" }])}>+ Add CC</Btn>
              </div>
              {ccRecipients.length === 0 && <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>CC recipients receive a copy of the signed document but do not need to sign.</div>}
              {ccRecipients.map((cc, i: number) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 6, alignItems: "end" }}>
                  <div>
                    <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 3, letterSpacing: ".04em", textTransform: "uppercase" }}>Name</label>
                    <input value={cc.name} onChange={e => { const arr = [...ccRecipients]; arr[i] = { ...arr[i], name: e.target.value }; setCcRecipients(arr); }} placeholder="Full Name" style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.txD, marginBottom: 3, letterSpacing: ".04em", textTransform: "uppercase" }}>Email</label>
                    <input value={cc.email} onChange={e => { const arr = [...ccRecipients]; arr[i] = { ...arr[i], email: e.target.value }; setCcRecipients(arr); }} placeholder="email@example.com" style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <button onClick={() => setCcRecipients(ccRecipients.filter((_: any, j: number) => j !== i))} style={{ padding: "8px 10px", border: "none", backgroundColor: T.redS, color: T.red, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>X</button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: `1px solid ${T.brd}`, paddingTop: 12 }}>
              <Btn v="secondary" sz="sm" onClick={() => setShowSignModal(false)}>Cancel</Btn>
              <Btn v="primary" sz="sm" onClick={() => { sendForSignature(); }} disabled={signLoading} style={{ backgroundColor: "#5C2D91" }}>{signLoading ? "Sending..." : `Send via ${signProvider === "signnow" ? "SignNow" : "DocuSign"}`}</Btn>
            </div>
          </div>
        </div>
      )}
      {showCancelSupersede && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => !csSubmitting && setShowCancelSupersede(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: T.bg, padding: 24, borderRadius: 8, width: 480, maxWidth: "92%" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.tx, marginBottom: 4 }}>Reassign to different client</div>
            <div style={{ fontSize: 12, color: T.txM, marginBottom: 16 }}>
              This cancels the current request and creates a new one in the selected client with the same metadata. The cancellation is permanent and cannot be undone — the original is retained as a historical record.
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 4 }}>New client *</div>
              <select value={csNewClientId} onChange={(e) => setCsNewClientId(e.target.value)} style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 13, fontFamily: "inherit" }}>
                <option value="">Select a client...</option>
                {csClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 4 }}>Reason * (min 10 chars)</div>
              <textarea value={csReason} onChange={(e) => setCsReason(e.target.value)} rows={3} placeholder="e.g., Misfiled — should have been Initech, not Acme" style={{ width: "100%", padding: "8px 10px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ marginBottom: 16, fontSize: 12, color: T.txM }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={csPreserve} onChange={(e) => setCsPreserve(e.target.checked)} />
                <span>Note attachment-preservation intent in audit log (v1: file copy not yet implemented)</span>
              </label>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: `1px solid ${T.brd}`, paddingTop: 12 }}>
              <Btn v="secondary" sz="sm" onClick={() => setShowCancelSupersede(false)} disabled={csSubmitting}>Cancel</Btn>
              <Btn v="primary" sz="sm" disabled={csSubmitting || !csNewClientId || csReason.trim().length < 10} onClick={async () => {
                setCsSubmitting(true);
                try {
                  const r = await apiCall(`/admin/requests/${request.id}/cancel-and-supersede`, {
                    method: "POST",
                    body: JSON.stringify({ new_client_id: Number(csNewClientId), reason: csReason.trim(), preserve_attachments: csPreserve }),
                  });
                  if (r && r.successor) {
                    setShowCancelSupersede(false);
                    onUpdate?.(request.id, { _refreshOnly: true });
                    onClose?.();
                    alert(`Reassignment complete. Successor: ${r.successor.client_display_code ? `${r.successor.client_display_code}-${String(r.successor.display_number).padStart(4,'0')}` : `request ${r.successor.id}`}`);
                  } else {
                    alert(r?.error || "Reassignment failed");
                  }
                } catch (e: any) {
                  alert(e?.message || "Reassignment failed");
                } finally {
                  setCsSubmitting(false);
                }
              }}>{csSubmitting ? "Reassigning..." : "Confirm reassignment"}</Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};